/** The English catalog (decision 34): every string the interface shows. Keys are grouped by where they show. */
import type { Catalog } from './i18n.ts';

export const EN = Object.freeze({
  'app.name': 'Cockatrice',

  // Decision 78: the Formatting page.
  'notice.templateImageGone': 'This template’s image is no longer in any book, so it now has no image.',
  'skip.format': 'Skip to the settings',
  'format.title': 'Formatting',
  'format.writing': 'Writing',
  'format.pages': 'Book pages',
  'format.templates': 'Templates',
  'format.new': 'New template',
  'format.newName': 'My template',
  'format.copyOf': '{name} copy',
  'format.preset.classic': 'Classic',
  'format.preset.elegant': 'Elegant',
  'format.preset.modern': 'Modern',
  'format.preset.large-print': 'Large print',
  'format.use': 'Use {name}',
  'format.star': 'Favourite {name}',
  'format.more': 'More for {name}',
  'format.duplicate': 'Duplicate',
  'format.delete': 'Delete',
  'format.rename': 'Rename',
  'format.renameTitle': 'Rename {name}',
  'format.name': 'Name',
  'format.cancel': 'Cancel',
  'format.deleteTitle': 'Delete {name}?',
  'format.deleteUnused': 'No book uses this template.',
  'format.deleteUsed': 'Used by {books}. They keep their look, but stop following a template.',
  'format.sampleNumber': 'Chapter One',
  'format.sampleTitle': 'The Beginning',
  'format.heading': 'Chapter heading',
  'format.headingOn': 'Show chapter headings',
  'format.lineNumber': 'Chapter number',
  'format.lineTitle': 'Chapter title',
  'format.lineSubtitle': 'Chapter subtitle or character name',
  'format.display': 'Number shows as',
  'format.display.arabic': '1',
  'format.display.word': 'One',
  'format.display.chapter-word': 'Chapter One',
  'format.display.chapter-arabic': 'Chapter 1',
  'format.display.roman': 'I',
  'format.display.none': 'No number',
  'format.font': 'Font',
  'format.fontNumber': 'Chapter number font',
  'format.fontTitle': 'Chapter title font',
  'format.fontSubtitle': 'Chapter subtitle font',
  'format.fontStar': 'Favourite {font}',
  'format.align': 'Alignment',
  'format.left': 'Left',
  'format.center': 'Centre',
  'format.right': 'Right',
  'format.style': 'Style',
  'format.bold': 'Bold',
  'format.italic': 'Italic',
  'format.caps': 'ALL CAPS',
  'format.size': 'Size',
  'format.points': '{size} pt',
  'format.fade': 'Fade',
  'format.fadeHelp': 'Stops at 70 %, so it can never vanish.',
  'format.titleGap': 'Space above the title',
  'format.subtitleGap': 'Space above the subtitle',
  'format.gapLess': 'Less: {label}',
  'format.gapMore': 'More: {label}',
  'format.ems': '{size} em',
  'format.image': 'Chapter image',
  'format.imagePerChapter': 'Use individual images',
  'format.upload': 'Upload',
  'format.gallery': 'Gallery',
  'format.yourImages': 'Your images',
  'format.yourImage': 'Your image',
  'format.noImages': 'Images you add to your books show here.',
  'format.ourImages': 'From us',
  'format.ornament.flourish': 'Flourish',
  'format.ornament.frame-corners': 'Corner frame',
  'format.ornament.leaf-spray': 'Leaf spray',
  'format.ornament.rule-diamond': 'Rule with a diamond',
  'format.ornament.rule-dots': 'Row of dots',
  'format.ornament.star-burst': 'Star burst',
  'format.imageRemove': 'Remove image',
  'format.imagePlace': 'Placement',
  'format.imageAbove': 'Above',
  'format.imageBetween': 'Between',
  'format.imageBelow': 'Below',
  'format.imageBehind': 'Behind',
  'format.bleed': 'Bleed image',
  'format.bleedHelp': 'For printed books only: the image runs off the page edge in the PDF. Digital editions don’t change.',
  'format.subtitleText': 'This chapter’s subtitle or character name',
  'format.subtitleHelp': 'Shown under the title of the chapter open now.',
  'app.loading': 'Opening your books…',
  'app.closing': 'Closed. Your writing is saved on this device.',

  // Part 36a: the account (cockatrice-ui `Onboarding.jsx`, `TrialEnded.jsx`; the cards the design lacks in its tone).
  'account.back': 'Back',
  'account.email': 'Email',
  'account.password': 'Password',
  'account.show': 'Show',
  'account.hide': 'Hide',
  'account.working': 'Checking…',
  'account.reload': 'Reload the app',
  'account.buy': 'Buy Cockatrice',
  'account.buyToWrite': 'Buy to keep writing',
  'account.seconds': { one: '{count} second', other: '{count} seconds' },
  'account.minutes': { one: '{count} minute', other: '{count} minutes' },
  'account.chip': { one: 'Trial · {count} day left', other: 'Trial · {count} days left' },
  'account.readOnly': 'Read only',
  'account.paused': 'Editing is paused. Your text is safe and nothing is lost; you can still read and export.',
  'account.welcome.title': 'Write your novel and see it as a book.',
  'account.welcome.lead': 'You write chapter by chapter, and beside the text you see real pages, updated as you type.',
  'account.welcome.local': 'Your books stay on this device.',
  'account.welcome.localRest': 'Your account holds your trial and purchase, never your writing.',
  'account.welcome.trial': 'Seven days free,',
  'account.welcome.trialRest': 'then one purchase. No subscription.',
  'account.welcome.start': 'Start your free trial',
  'account.welcome.have': 'I already have an account',
  'account.welcome.notNow': 'Not now',
  'account.welcome.notNowRest': '— read and export the books on this device without an account.',
  'account.create.title': 'Create your account',
  'account.create.lead': 'Your seven-day trial starts when you confirm your email.',
  'account.create.agreeStart': 'I accept the',
  'account.create.terms': 'Terms',
  'account.create.agreeMiddle': 'and have read the',
  'account.create.privacy': 'Privacy policy',
  'account.create.agreeEnd': '.',
  'account.create.submit': 'Start 7-day trial',
  'account.create.have': 'Already have an account?',
  'account.strength.empty': 'At least 10 characters. A few unrelated words work well.',
  'account.strength.short': 'Too short. Keep going.',
  'account.strength.weak': 'Too easy to guess. Add another unrelated word.',
  'account.strength.good': 'Good.',
  'account.strength.strong': 'Strong.',
  'account.login.title': 'Log in',
  'account.login.lead': 'Log in on each device where you write, so it can use your purchase.',
  'account.login.forgot': 'Forgot password?',
  'account.login.submit': 'Log in',
  'account.login.note': 'Your books do not come with your account. On a new device, import a bundle to bring them across.',
  'account.login.new': 'New to Cockatrice?',
  'account.login.create': 'Create an account',
  'account.check.title': 'Check your email',
  'account.check.lead': 'We sent a link to {email}.',
  'account.check.rest': 'Open it on the device you want to write on, within 24 hours, to finish creating your account. If nothing arrives, check your spam folder.',
  'account.verify.title': 'Finish creating your account',
  'account.verify.lead': 'Type the email and password you signed up with. Your trial starts now.',
  'account.verify.submit': 'Finish',
  'account.verify.homeScreen': 'This link opened in the browser, not in Cockatrice on your Home Screen. Finish here, then log in once in the Home Screen app too.',
  'account.verify.later': 'Not now',
  'account.verify.other': 'This device is logged in to {email}. Log out in Settings first to use this link for another account.',
  'account.verify.keep': 'Back to my books',
  'account.second.title': 'Second sign-in step',
  'account.second.lead': 'Your account has a second sign-in step. Use your passkey, or type a code.',
  'account.second.passkey': 'Use your passkey',
  'account.second.codeLabel': 'Code from your authenticator app',
  'account.second.backupLabel': 'Backup code',
  'account.second.useBackup': 'Use a backup code instead',
  'account.second.useApp': 'Use a code from the app instead',
  'account.second.used': 'You used a backup code',
  'account.second.left': { one: 'You have {count} backup code left. Each one works once.', other: 'You have {count} backup codes left. Each one works once.' },
  'account.second.continue': 'Continue',
  'account.error.email': 'Type your email address, like name@example.com.',
  'account.error.password': 'Type your password.',
  'account.error.weak': 'Choose a stronger password: at least 10 characters that are hard to guess.',
  'account.error.agree': 'To create an account, accept the Terms and the Privacy policy.',
  'account.error.noCode': 'Type the code.',
  'account.error.wrongLogin': 'Wrong email or password.',
  'account.error.tooMany': 'Too many tries. Wait a little, then try again.',
  'account.error.wait': 'Too many tries. Try again in {wait}.',
  'account.error.devices': 'This account is logged in on too many devices. Log out on one of them first.',
  'account.error.code': "That code didn't work. Check it and try again.",
  'account.error.ticket': 'That took too long. Log in again.',
  'account.error.outdated': 'This version of the app is out of date. Reload it, then try again.',
  'account.error.link': "This link has expired, was used already, or the password doesn't match. Log in, or sign up again.",
  'account.error.passkeyOff': "Passkeys aren't available right now. Use a code instead.",
  'account.error.passkey': "The passkey didn't answer. Try again, or use a code.",
  'account.error.unsafe': 'The server asked for unsafe settings, so nothing was sent. Try again later.',
  'account.error.failed': "Couldn't reach Cockatrice's server. Check your connection and try again.",
  'account.banner.label': 'Account notice',
  'account.banner.none.title': "You're reading without an account.",
  'account.banner.none.text': 'You can read and export your books. Start your free trial or log in to write.',
  'account.banner.logged-out.title': "You're logged out.",
  'account.banner.logged-out.text': 'Log in to see your books on this device, or start your free trial.',
  'account.banner.ended.title': 'Your trial has ended.',
  'account.banner.ended.text': 'You can still read and export. Buy Cockatrice to keep writing.',
  'account.banner.unknown.title': "Your trial isn't confirmed on this device yet.",
  'account.banner.unknown.text': 'You can read and export. Connect to the internet to keep writing.',
  'account.banner.signed-out.title': 'This device was logged out.',
  'account.banner.signed-out.text': 'Log in again to keep your trial or purchase up to date here.',
  'account.banner.purge': 'Unless you buy, your account is deleted from our server on {date}. Your books on this device stay.',
  'account.banner.shares': 'Unless you buy, your share links and shared books end on {date}. Books shared with you stay.',
  'account.banner.alsoSignedOut': 'This device was logged out; log in again to see a purchase.',
  // Part 36c: the account page, its sheets, the banners every device shows and the mailed links' cards.
  'account.error.grant': 'That took too long. Type your password again.',
  'account.error.session': 'This device was logged out of your account. Log in again, then try again.',
  'account.error.factorOff': 'Your account has no second sign-in step any more. Look again, then try again.',
  'account.error.factorOn': 'Your account already has a second sign-in step.',
  'account.error.notFound': "That isn't there any more. Look again, then try again.",
  'account.error.cancelHere': 'Only the device that asked for this change, or one with your second sign-in step, can cancel it.',
  'account.error.sameEmail': 'That is the address your account has now.',
  'account.error.wrongPassword': 'Wrong password.',
  'account.error.linkHere': 'This link has ended, was used already, or was opened on another device. Open it on the device where you asked, within the hour.',
  'account.error.resetLink': 'This link has ended or was used already. Ask for a new one.',
  'account.error.passkeyMake': "The passkey wasn't made. Try again, or use an authenticator app.",
  'account.error.saved': 'Tick that you have saved the codes first.',
  'account.error.export': "Couldn't save the file. Try again.",
  'account.page.factor': 'Second sign-in step',
  'account.page.devices': 'Devices',
  'account.page.devicesHelp': 'Where you are logged in. We email you whenever a new device logs in.',
  'account.page.waiting': 'Waiting changes',
  'account.page.waitingHelp': 'Changes to your account that wait for a link or a time. Any you did not ask for, cancel.',
  'account.page.waitingNone': 'Nothing is waiting.',
  'account.page.activity': 'Recent activity',
  'account.page.activityNone': 'Nothing yet.',
  'account.page.data': 'Your data',
  'account.page.loading': 'Loading…',
  'account.page.failed': "Couldn't load this.",
  'account.page.retry': 'Try again',
  'account.page.passwordHelp': 'Changing it logs out your other devices.',
  'account.page.changePassword': 'Change password',
  'account.page.changeEmail': 'Change email',
  'account.page.terms': 'Terms of service and privacy policy',
  'account.page.termsHelp': 'You accepted the current versions.',
  'account.page.termsRead': 'Read them',
  'account.factor.off': 'Off',
  'account.factor.recommended': 'Recommended',
  'account.factor.offHelp': 'A passkey, or a code from an app on your phone. Without it, someone with your email and password could take over your account and your purchase, though never your books. It is required to turn on sync.',
  'account.factor.setUp': 'Set up',
  'account.factor.on': 'On',
  'account.factor.onHelp': 'Logging in on a new device asks for it as well as your password.',
  'account.factor.app': 'Authenticator app',
  'account.factor.passkey': 'Passkey',
  'account.factor.added': 'Added {date}',
  'account.factor.codes': { one: '{count} backup code left', other: '{count} backup codes left' },
  'account.factor.codesHelp': 'Each one lets you in once if you lose your second step.',
  'account.factor.remove': 'Remove',
  'account.factor.removeApp': 'Remove the authenticator app',
  'account.factor.removePasskey': 'Remove the passkey added {date}',
  'account.factor.addPasskey': 'Add a passkey',
  'account.factor.replaceApp': 'Use another app',
  'account.factor.addApp': 'Add an authenticator app',
  'account.factor.newCodes': 'New backup codes',
  'account.device.this': 'This device',
  'account.device.name': 'Device added {date}',
  'account.device.used': 'Last used {date}',
  'account.device.new': 'New',
  'account.device.removed': 'Removed',
  'account.device.loggedOut': 'Logged out',
  'account.device.remove': 'Remove',
  'account.device.removeName': 'Remove the device added {date}',
  'account.change.password': 'A new password',
  'account.change.email': 'A new email address',
  'account.change.revoke': "A device's removal",
  'account.change.factor-remove': 'A second sign-in step removed',
  'account.change.factor-add': 'A second sign-in step added',
  'account.change.backup-codes': 'New backup codes',
  'account.change.reset': 'A password reset',
  'account.change.open': 'Waiting for the link we emailed, until {date}.',
  'account.change.pending': 'Takes effect on {date}.',
  'account.change.here': 'Asked on this device.',
  'account.change.there': 'Asked on another device.',
  'account.change.cancel': 'Cancel',
  'account.change.cancelName': 'Cancel {change}',
  'account.notice.new-device': 'A new device logged in',
  'account.notice.second-factor-on': 'A second sign-in step was turned on',
  'account.notice.second-factor-failed': 'A wrong code was typed for the second sign-in step',
  'account.notice.backup-code-used': 'A backup code was used',
  'account.notice.change-pending': 'A change is waiting',
  'account.notice.password-changed': 'The password was changed',
  'account.notice.email-changed': 'The email address was changed',
  'account.notice.device-revoked': 'A device was removed',
  'account.notice.change-cancelled': 'A change was cancelled',
  'account.notice.change-void': "A change couldn't be made",
  'account.notice.reset-pending': 'A password reset was asked for',
  'account.notice.password-reset': 'The password was reset',
  'account.notice.second-factor-added': 'A second sign-in step was added',
  'account.notice.second-factor-removed': 'A second sign-in step was removed',
  'account.notice.second-factor-off': 'The second sign-in step was turned off',
  'account.notice.backup-codes-replaced': 'New backup codes were made',
  'account.notice.deletion-pending': 'Deleting the account was asked for',
  'account.notice.deletion-cancelled': "The account's deletion was cancelled",
  'account.notice.purge-warning': 'A warning that the account will be deleted',
  'account.notice.shares-warning': 'A warning that your share links and shared books will end; books shared with you stay',
  'account.notice.other': 'Something changed in your account',
  'account.notice.when': '{date}, {where}',
  'account.notice.here': 'this device',
  'account.notice.there': 'another device',
  'account.notice.nowhere': 'by email',
  'account.data.export': 'Export all data',
  'account.data.exportHelp': 'Everything our server holds about you, as a file. Your books are in your backups and bundles, which never leave this device unless you send them.',
  'account.data.save': 'Save {name}',
  'account.data.exported': 'Saved {name}.',
  'account.data.delete': 'Delete account',
  'account.data.deleteHelp': 'Deletes your account and ends your purchase. Once the deletion is final, in 30 days, the books of this account on your devices are deleted too. Backups you saved outside the app stay.',
  'account.data.deleteOpen': 'Delete account…',
  'account.delete.title': 'Delete your account?',
  'account.delete.server': 'Your account is deleted from our server in 30 days. Until then, any of your devices, or the link we email you, can cancel it.',
  'account.delete.purchase': 'Your purchase ends. It cannot be moved to another account.',
  'account.delete.books': 'Then the books of this account are deleted on this and your other devices. Export them first: backups and bundles you saved outside the app stay.',
  'account.delete.password': 'Type your password to confirm',
  'account.delete.cancel': 'Cancel',
  'account.delete.submit': 'Delete account',
  'account.delete.asked': 'Your account will be deleted on {date}. We emailed you a link to cancel it.',
  'account.sheet.continue': 'Continue',
  'account.sheet.done': 'Done',
  'account.sheet.notNow': 'Not now',
  'account.sheet.cancel': 'Cancel',
  'account.sheet.step': 'Step {step} of {total}',
  'account.sheet.security': 'Account security',
  'account.proof.lead': 'Type your password to confirm it is you.',
  'account.proof.factorLead': 'Your account has a second sign-in step. Use your passkey, or type a code.',
  'account.proof.forgot': 'Forgot your password? Log out, then use Forgot password? on the login.',
  'account.password.title': 'Change your password',
  'account.password.current': 'Current password',
  'account.password.new': 'New password',
  'account.password.submit': 'Change password',
  'account.email.title': 'Change your email',
  'account.email.new': 'New email address',
  'account.email.submit': 'Change email',
  'account.revoke.title': 'Remove this device?',
  'account.revoke.lead': 'The device added {date} is logged out and stops using your purchase. Its books stay on it.',
  'account.revoke.submit': 'Remove device',
  'account.cancel.title': 'Cancel this change?',
  'account.cancel.lead': '{change}: if you did not ask for it, cancelling it keeps your account as it is.',
  'account.cancel.submit': 'Cancel the change',
  'account.cancel.done': 'The change is cancelled.',
  'account.stopReset.title': 'Stop this reset?',
  'account.stopReset.lead': 'Your password stays as it is.',
  'account.stopReset.submit': 'Stop this reset',
  'account.stopReset.done': 'The reset was stopped. Your password stays the same. We recommend turning on a second sign-in step.',
  'account.keepAccount.title': 'Keep your account?',
  'account.keepAccount.lead': 'Cancelling the deletion keeps your account, your purchase and your books.',
  'account.keepAccount.submit': 'Cancel the deletion',
  'account.keepAccount.done': 'The deletion is cancelled. Your account stays.',
  'account.removeFactor.title': 'Remove this second step?',
  'account.removeFactor.lead': 'You can add another at any time.',
  'account.removeFactor.last': 'It is your only second step: your account goes back to the password alone, and your backup codes stop working.',
  'account.removeFactor.submit': 'Remove',
  'account.codes.title': 'New backup codes',
  'account.codes.lead': 'New codes replace the old ones, which stop working.',
  'account.codes.submit': 'Make new codes',
  'account.codes.from': 'They count from {date}; until then, your old codes still work.',
  'account.outcome.applied': 'The change is made.',
  'account.outcome.password': 'Your password is changed. Your other devices are logged out: log in there with the new one.',
  'account.outcome.email': 'Your email address is changed.',
  'account.outcome.revoke': 'The device is removed. Its books stay on it.',
  'account.outcome.factorAdded': 'The second sign-in step is added.',
  'account.outcome.factorRemoved': 'The second sign-in step is removed.',
  'account.outcome.codes': 'Your new codes work from now on.',
  'account.outcome.checkTitle': 'Check your email',
  'account.outcome.check': 'We emailed a link to {email}. Open it on this device within the hour to finish.',
  'account.outcome.checkNew': 'We emailed a link to {email}. Open it on this device within the hour to confirm the new address.',
  'account.outcome.checkNext': 'We emailed a link to your new address. Open it on this device within the hour to confirm it.',
  'account.outcome.pending': 'This device is new to your account, so this takes effect on {date}. Until then, your other devices can cancel it.',
  'account.outcome.void': "This change couldn't be made. Look at your account, then try again.",
  'account.tf.title.choose': 'Add a second sign-in step',
  'account.tf.title.passkey': 'Create a passkey',
  'account.tf.title.app': 'Connect an authenticator app',
  'account.tf.title.codes': 'Save your backup codes',
  'account.tf.title.done': 'Second step is on',
  'account.tf.choose': 'A second step makes it much harder for someone else to take over your account. It is optional for now, and needed before you turn on sync.',
  'account.tf.choiceLabel': 'Second step',
  'account.tf.passkeyTitle': 'Passkey',
  'account.tf.passkeyText': 'Your computer or phone confirms it is you, with a fingerprint, face or device PIN. Nothing to type.',
  'account.tf.appTitle': 'Authenticator app',
  'account.tf.appText': 'An app on your phone shows a new six-digit code every 30 seconds. Works with most authenticator apps.',
  'account.tf.recommended': 'Recommended',
  'account.tf.checkEmail': 'We emailed a link to {email}. Open it on this device within the hour to go on.',
  'account.tf.passkey': 'When you press Create passkey, your browser or system will open its own window. Follow it to confirm with your fingerprint, face or device PIN.',
  'account.tf.passkeyNote': 'The passkey is kept by your device or password manager, never by us. If you use several devices, you can add a passkey on each later.',
  'account.tf.passkeySubmit': 'Create passkey',
  'account.tf.app': 'Scan this code with your authenticator app, then type the six-digit code it shows.',
  'account.tf.qr': 'QR code for your authenticator app',
  'account.tf.manual': "Can't scan? Enter this key",
  'account.tf.openApp': 'Open in an app on this device',
  'account.tf.code': 'Code from the app',
  'account.tf.appSubmit': 'Verify code',
  'account.tf.codes': 'If you lose your second step, each of these codes lets you in once. Keep them somewhere safe and away from this device, like a printed page in a drawer.',
  'account.tf.codesLabel': 'Backup codes',
  'account.tf.download': 'Download',
  'account.tf.copy': 'Copy',
  'account.tf.copied': 'Copied.',
  'account.tf.print': 'Print',
  'account.tf.saved': 'I have saved these codes',
  'account.tf.done': 'From now on, logging in on a new device asks for your second step as well as your password. You can now turn on sync whenever you like.',
  'account.tf.codesFile': 'Cockatrice backup codes for {email}',
  'account.banner.reset.title': 'A password reset was asked for on {date}.',
  'account.banner.reset.text': 'It takes effect on {date}. Not you? Stop this reset, and nothing changes.',
  'account.banner.reset.stop': 'Stop this reset',
  'account.banner.deletion.title': 'Your account will be deleted on {date}.',
  'account.banner.deletion.text': 'The books of this account on this device are deleted with it. Export them first, or cancel the deletion.',
  'account.banner.deletion.cancel': 'Cancel deletion',
  'account.banner.deletion.export': 'Back up books',
  'account.banner.change.label': 'Waiting: {change}',
  'account.banner.change.title': '{change} is waiting.',
  'account.banner.change.text': 'It takes effect on {date}. Not you? Cancel it.',
  'account.banner.newDevice.title': 'A new device logged in to your account on {date}.',
  'account.banner.newDevice.text': 'Not you? Remove it in Settings, then change your password.',
  'account.banner.newDevice.label': 'New device',
  'account.banner.newDevice.review': 'Review devices',
  'account.banner.newDevice.seen': 'Seen',
  'account.banner.terms.title': "We've updated the terms of service or the privacy policy.",
  'account.banner.terms.text': 'Read them, then accept. Nothing stops in the meantime.',
  'account.banner.terms.label': 'Updated terms',
  'account.banner.terms.accept': 'Accept',
  'account.forgot.title': 'Reset your password',
  'account.forgot.lead': "Type your email. We'll send a link to choose a new password. The new password takes effect after 72 hours, and every device logged in to your account can stop it.",
  'account.forgot.submit': 'Send link',
  'account.forgot.check': 'If an account has {email}, we sent it a link. Open it within the hour.',
  'account.reset.title': 'Choose a new password',
  'account.reset.lead': 'It takes effect in 72 hours. Until then, your devices can stop it, and your current password still works.',
  'account.reset.factor': "I've lost my second sign-in step too",
  'account.reset.factorHelp': 'It is removed with the reset; add a new one once you log in.',
  'account.reset.submit': 'Reset password',
  'account.reset.pending': 'Your password changes on {date}. Then log in with the new one.',
  'account.confirm.title': 'Confirm the change',
  'account.confirm.lead': 'This confirms the change you asked for on this device.',
  'account.confirm.submit': 'Confirm',
  'account.confirm.noAccount': 'Log in on the device where you asked for this change, then open the link there.',
  'account.enrol.title': 'Set up your second sign-in step',
  'account.enrol.lead': 'Go on with the second step you started on this device.',
  'account.enrol.noAccount': 'Log in on the device where you started setting up your second step, then open the link there.',
  'account.keepLink.title': "Cancel your account's deletion?",
  'account.keepLink.lead': 'Your account, your purchase and your books stay.',
  'account.keepLink.submit': 'Cancel the deletion',
  'account.keepLink.done': 'The deletion is cancelled. Your account stays.',
  'account.link.bad': "This link isn't complete. Open it again from the email.",
  'account.link.badTitle': "This link can't be used",
  'account.paste.label': 'Or paste the link from the email',
  'account.paste.help': 'In the app on your Home Screen, links from email open in the browser instead: in the email, press and hold the link, choose Copy Link, then paste it here.',
  'account.paste.title': 'A link from our email',
  'account.paste.rowHelp': 'To confirm a change or finish a second step on this device, paste the link from the email.',
  'account.paste.open': 'Open link',
  'account.paste.bad': "That isn't a link from our email. Copy the whole link, then try again.",
  'account.link.homeScreen': 'This link opened in the browser, not in Cockatrice on your Home Screen, where you asked for this. In the email, press and hold the link and choose Copy Link, then paste it in Cockatrice on your Home Screen: Settings, Account, Waiting changes.',

  'home.nav': 'Main',
  'home.myBooks': 'My books',
  'home.sharing': 'Sharing',
  'home.shared': 'Shared with me',
  'home.later': '(Coming later)',
  'home.laterName': '{name} (Coming later)',
  'home.search': 'Search books and chapters',
  'home.import': 'Import…',
  'home.importFile': 'Word or Markdown file…',
  'home.importFileHint': 'Becomes a new book, chapter by chapter',
  'home.importBundle': 'Bundle…',
  'home.importBundleHint': 'A book saved from Cockatrice, back exactly',
  'home.newEmpty': 'Empty book',
  'home.newEmptyHint': 'A new book, opened in the writer',
  'home.settings': 'Settings',
  'home.account': 'Account',
  'home.eyebrow': 'Pick up where you left off',
  'home.chapter': 'Chapter {number} · {title}',
  'home.edited': '{where} — last edited {when}',
  'home.today': 'today at {time}',
  'home.yesterday': 'yesterday at {time}',
  'home.onDay': 'on {date}',
  'home.words': { one: '{count} word', other: '{count} words' },
  'home.goal': 'Goal {goal}',
  'home.progress': 'Progress toward the book goal',
  'home.progressText': '{words} of {goal} words',
  'home.continue': 'Continue writing',
  'home.export': 'Export',
  'home.details': 'Book details',
  'home.sort': 'Sort',
  'home.sortEdited': 'Last edited',
  'home.sortTitle': 'Title',
  'home.sortWords': 'Word count',
  'home.newTile': 'New book',
  'home.newTileHint': 'or import Word / bundle',
  'home.sharedHint': 'Books you can read or comment on',
  'home.skip': 'Skip to your books',
  'home.library': 'Library',

  // Part 38c: books shared with the writer (cockatrice-ui `Library.jsx` Shared with me; `EditorView.jsx`, read only).
  'shared.emptyTitle': 'Nothing is shared with you yet.',
  'shared.empty': 'When a writer invites you, open their invite link on this device.',
  'shared.untitled': 'Untitled book',
  'shared.by': 'by {author}',
  'shared.role.editor': 'Editor',
  'shared.role.beta-reader': 'Beta reader',
  'shared.role.proofreader': 'Proofreader',
  'shared.ends': 'Access ends {date}',
  'shared.open': 'Open {title}',
  'shared.finish': 'Its key isn’t on this device yet.',
  'shared.unlock.button': 'Open with your password',
  'shared.unlock.title': 'Open your shared books',
  'shared.unlock.lead': 'Type your password to open the books shared with you on this device.',
  'shared.unlock.submit': 'Open',
  'shared.unlock.done': 'Your shared books are open on this device.',
  'shared.unlock.none': 'There is nothing to open yet. A book appears here once its invite is accepted.',
  'shared.waiting': 'The writer hasn’t sent the text yet.',
  'shared.offline': 'You’re offline. The copies on this device are shown.',
  'shared.unavailable': 'A shared book is temporarily unavailable; the copy on this device is shown.',
  'shared.check': 'Check for updates',
  'shared.checking': 'Checking…',
  'shared.trial.title': 'Want to write your own books?',
  'shared.trial.lead': 'Start a free 7-day trial on this account. The books shared with you stay.',
  'shared.trial.start': 'Start your free trial',
  'shared.trial.failed': 'The trial couldn’t start. Check your connection and try again.',
  'join.accept.title': 'You’re invited to read a book',
  'join.accept.lead': 'A writer shared a book with you. Accept with your password to open it on this device.',
  'join.accept.button': 'Accept the invite',
  'join.accept.notNow': 'Not now',
  'join.accept.working': 'Opening the invite…',
  'join.accept.done': 'Invite accepted. The book is in Shared with me.',
  'join.accept.gone': 'No invite is waiting here any more: it was accepted or put away in another tab. Shared with me lists what is shared with you.',
  'join.create.title': 'Create your free account to read',
  'join.create.lead': 'A writer invited you to read their book. The account is free and holds only the books shared with you.',
  'join.create.submit': 'Create my free account',
  'join.login.lead': 'Log in to accept the invite.',
  'join.incomplete': 'This invite link is incomplete. Copy the whole link from the writer’s message.',
  'join.off': 'Invites need an account, and this version of the app has none.',
  'join.error.invite-address': 'This invite is for another email address. Log in with the address the writer invited.',
  'join.error.invite-claimed': 'Someone already accepted this invite. Ask the writer for a new one.',
  'join.error.invite-ended': 'This invite has ended. Ask the writer for a new one.',
  'join.error.invite-gone': 'This invite link isn’t valid. Ask the writer for a new one.',
  'join.error.change-pending': 'Finish or cancel your password change first, then accept the invite.',
  'join.error.keyring': 'Your shared books can’t be opened with this password. Ask the writer for a new invite.',
  'join.error.failed': 'The invite couldn’t be accepted just now. It’s kept on this device; try again.',
  'read.back': 'Shared with me',
  'read.by': 'by {author}',
  'read.badge': 'Read · Notes: {notes}',
  'read.notes.none': 'None',
  'read.notes.read': 'Read',
  'read.notes.write': 'Write',
  'read.youAre.editor': 'You are an editor on this book.',
  'read.youAre.beta-reader': 'You are a beta reader on this book.',
  'read.youAre.proofreader': 'You are a proofreader on this book.',
  'read.can.none': 'You can read the manuscript. The text is locked.',
  'read.can.read': 'You can read the manuscript and its notes, and write to the writer. The text is locked.',
  'read.can.write': 'You can read the manuscript and add notes. The text is locked.',
  'read.chapters': 'Chapters',
  'read.missing': { one: '{count} chapter isn’t on this device yet', other: '{count} chapters aren’t on this device yet' },
  'read.kicker': 'Chapter {number}',
  'read.empty': 'The writer hasn’t sent this book’s text yet. It shows here once they have.',
  'read.gone': 'This book is no longer shared with you.',
  'read.manuscript': 'Manuscript',
  'read.layout': 'Layout',
  'read.text': 'Text',
  'read.pages': 'Pages',
  'read.notesButton': 'Notes',
  'read.closeNotes': 'Close notes',
  'read.tab.chapter': 'This chapter',
  'read.tab.notebook': 'Notebook',
  'read.notebookCount': '{count} shared',
  'read.private': 'The writer’s private notes are not shown.',
  'read.noNotes': 'No notes here yet.',
  'read.composer': 'New note',
  'read.placeholder': 'Select text to anchor a note, or note the whole chapter',
  'read.placeholderNotebook': 'Write a note for the whole book',
  'read.toWriter': 'Only the writer sees your notes.',
  'read.toGroup': 'The writer and the other {group} see your notes.',
  'read.group.editor': 'editors',
  'read.group.beta-reader': 'beta readers',
  'read.group.proofreader': 'proofreaders',
  'read.post': 'Post note',
  'read.reply': 'Reply',
  'read.replyLabel': 'Reply to this note',
  'read.edit': 'Edit',
  'read.delete': 'Delete',
  'read.deleteAsk': 'Delete this note? Everyone who can see it loses it.',
  'read.save': 'Save',
  'read.cancel': 'Cancel',
  'read.you': 'You',
  'read.writer': 'Writer',
  'read.reader.editor': 'An editor',
  'read.reader.beta-reader': 'A beta reader',
  'read.reader.proofreader': 'A proofreader',
  'read.reader.everyone': 'A reader',
  'read.reader.person': 'A reader',
  'read.addNote': 'Add note',
  'read.copy': 'Copy',
  'read.selection': 'Selected text',
  'read.posted': 'Note posted.',
  'read.saved': 'Note saved.',
  'read.deleted': 'Note deleted.',
  'read.noteFailed': 'The note couldn’t be sent. Check your connection and try again.',
  'read.noteRefused': 'The note wasn’t taken: {reason}',
  'read.rateLimited': 'You’ve written many notes this hour. Try again in a while.',
  'read.notesFull': 'You’ve reached the limit of notes on this book.',
  'read.notesOff': 'The writer has turned notes off for you.',
  'read.whole': 'Whole chapter',
  'read.export': 'Export',

  'writer.chapter': '{number} · {title}',
  'writer.number': '{number}',
  'writer.crumbSep': '/',
  'writer.layout': 'Layout',
  'writer.side': 'Side by side',
  'writer.pages': 'Pages',
  'writer.text': 'Text',
  'writer.view': 'View',
  'writer.more': 'More',
  'writer.history': 'History',
  'writer.share': 'Share',
  'writer.reimport': 'Re-import Word file…',
  'writer.reimportHint': 'Bring back an edited Word export of this book',
  'writer.chapters': 'Chapters',
  'writer.railFoot': { one: 'Drag to reorder · {count} word', other: 'Drag to reorder · {count} words' },
  'writer.format': 'Formatting',
  'writer.bold': 'Bold',
  'writer.italic': 'Italic',
  'writer.smallCaps': 'Small caps',
  'writer.quote': 'Quote',
  'writer.boldMark': 'B',
  'writer.italicMark': 'I',
  'writer.smallCapsMark': 'Sc',
  'writer.quoteMark': '“',
  'writer.kicker': 'Chapter {number}',

  'export.pdf': 'PDF',
  'export.pdfHint': 'Print and proofs, exactly as previewed',
  'export.epub': 'EPUB',
  'export.epubHint': 'E-readers and stores',
  'export.word': 'Word…',
  'export.wordHint': 'Including standard manuscript format',
  'export.bundle': 'Bundle',
  'export.bundleHint': 'Chapters, pictures, notes and history. Imports back exactly.',

  'modal.close': 'Close',

  'details.title': 'Book details',
  'details.created': 'created {date}',
  'details.save': 'Save changes',
  'details.exportBundle': 'Export bundle',

  'newBook.title': 'New book',
  'newBook.tabs': 'Start from',
  'newBook.blank': 'Start blank',
  'newBook.import': 'Import a file',
  'newBook.create': 'Create book',
  'newBook.importAction': 'Import',
  'newBook.cancel': 'Cancel',
  'newBook.private': 'Saved on this device. Only you can open it.',
  'newBook.bookTitle': 'Title',
  'newBook.subtitle': 'Subtitle',
  'newBook.author': 'Author name',
  'newBook.authorHint': 'As it appears on the cover and title page. A pen name is fine.',
  'newBook.language': 'Language',
  'newBook.languageHint': 'Sets quotes and hyphenation',
  'newBook.goal': 'Word goal',
  'newBook.goalPlaceholder': 'e.g. 80000',
  'newBook.goalRefused': 'A word goal is a whole number from 1 to {max}.',
  'newBook.drop': 'Drop a file here',
  'newBook.dropHint': 'A Word or Markdown file, or a Cockatrice bundle (.zip)',
  'newBook.choose': 'Choose file…',
  'newBook.chosen': 'Chosen: {name}',
  'newBook.wordNote': 'Word or Markdown',
  'newBook.wordNoteText': 'Each Heading 1 becomes a chapter. Italics, bold, small caps and pictures come across. You check the split before it is saved.',
  'newBook.bundleNote': 'Bundle',
  'newBook.bundleNoteText': 'Brings back a book exactly as exported, with its notes, pictures and history.',

  'cover.type': 'Cover type',
  'cover.colourTab': 'Colour',
  'cover.photoTab': 'Photo',
  'cover.colours': 'Cover colour',
  'cover.colour.harbour': 'Harbour',
  'cover.colour.rust': 'Rust',
  'cover.colour.linen': 'Linen',
  'cover.colour.moss': 'Moss',
  'cover.colour.dusk': 'Dusk',
  'cover.colour.ochre': 'Ochre',
  'cover.upload': 'Upload a photo',
  'cover.photoHint': 'JPEG, PNG or WebP. Best at 1600 × 2560 px, the size Kindle asks for.',
  'cover.photo': 'Your cover photo',
  'cover.replace': 'Replace…',
  'cover.remove': 'Remove',
  'cover.adding': 'Adding the photo…',
  'cover.refused.too-large': 'That photo is larger than 50 MB.',
  'cover.refused.too-big': 'That photo is larger than 16,384 pixels a side or 50 megapixels.',
  'cover.refused.unsupported': 'That isn’t a photo the app takes. Use a JPEG, PNG or WebP file.',
  'cover.refused.damaged': 'That photo couldn’t be read. The file may be damaged.',
  'cover.refused.full': 'This book’s images would pass 100 MB, so the photo wasn’t added.',
  'cover.refused.failed': 'The photo couldn’t be added. Try again, or pick another.',
  'cover.madeWithout': 'Your book was made, but its photo couldn’t be added. {reason}',

  'elsewhere.title': 'Cockatrice is open in another tab',
  'elsewhere.body': 'Only one tab edits at a time, so nothing is overwritten. Use this tab instead, and the other one stops editing.',
  'elsewhere.useHere': 'Use here',
  'elsewhere.failed': "This tab couldn't take over. Close the other tab, or try again.",

  'failed.title': "Cockatrice can't open here",
  'failed.locks': "This browser can't keep two tabs from editing at once, so Cockatrice can't run safely in it. Update it, or use another browser.",
  'failed.store': "The browser wouldn't open the storage on this device. Check that this site may store data, then try again.",
  'failed.retry': 'Try again',
  'failed.kept': 'The open chapter is still below, as it was. Copy anything you need before you try again: trying again reloads the page.',

  'outdated.title': 'A newer version of Cockatrice is open',
  'outdated.body': 'Another tab updated Cockatrice. Reload to keep writing; everything typed here is saved.',
  'outdated.reload': 'Reload',

  'toolbar.showChapters': 'Show chapters',
  'toolbar.hideChapters': 'Hide chapters',
  'toolbar.focus': 'Focus mode',
  'toolbar.leaveFocus': 'Leave focus mode',
  'toolbar.settings': 'Settings',
  'toolbar.help': 'Help',
  'toolbar.chapter': 'Chapter: {title}',
  'toolbar.showPreview': 'Show preview',
  'toolbar.hidePreview': 'Hide preview',
  'toolbar.preview': 'Preview',
  'toolbar.text': 'Text',

  'preview.label': 'Preview',
  'preview.profile': 'Page size',
  'preview.profile.generic-6in': '6-inch e-reader',
  'preview.profile.kindle': 'Kindle Paperwhite',
  'preview.profile.phone': 'Phone',
  'preview.profile.tablet': 'Tablet',
  'preview.profile.eink-8in': '8-inch e-reader',
  'preview.profile.eink-10in': '10-inch e-reader',
  'preview.profile.pdf-a5': 'A5 PDF',
  'preview.deviceChosen': 'Page size: {device}',
  'preview.devices': 'Devices',
  'preview.favourites': 'Favourites',
  'preview.favourite': 'Favourite {device}',
  'preview.brand.general': 'General',
  'preview.brand.kindle': 'Kindle',
  'preview.brand.kobo': 'Kobo',
  'preview.brand.apple': 'Apple',
  'preview.brand.android': 'Android',
  'preview.brand.eink': 'BOOX and other E Ink',
  'preview.device.general-6in': '6-inch e-reader',
  'preview.device.general-a5': 'A5 PDF',
  'preview.device.kindle': 'Kindle',
  'preview.device.kindle-paperwhite': 'Kindle Paperwhite',
  'preview.device.kindle-colorsoft': 'Kindle Colorsoft',
  'preview.device.kindle-scribe': 'Kindle Scribe',
  'preview.device.kobo-clara': 'Kobo Clara',
  'preview.device.kobo-libra': 'Kobo Libra',
  'preview.device.kobo-sage': 'Kobo Sage',
  'preview.device.kobo-elipsa': 'Kobo Elipsa',
  'preview.device.ipad': 'iPad',
  'preview.device.iphone': 'iPhone',
  'preview.device.galaxy-tab': 'Samsung Galaxy Tab tablets',
  'preview.device.galaxy-phone': 'Samsung Galaxy phones',
  'preview.device.pixel-phone': 'Google Pixel phones',
  'preview.device.android-tablet': 'Other Android tablets',
  'preview.device.android-phone': 'Other Android phones',
  'preview.device.onyx-boox': 'Onyx BOOX',
  'preview.device.pocketbook': 'PocketBook',
  'preview.device.remarkable': 'reMarkable',
  'preview.device.bigme': 'Bigme',
  'preview.device.tolino': 'tolino',
  'preview.device.nook': 'Nook',
  'preview.approximate': 'Approximate: each e-reader sets its own lines.',
  'preview.exact': 'Exact: the PDF has these pages.',
  'preview.loading': 'Setting the pages…',
  'preview.none': 'Open a chapter to see its pages.',
  'preview.failed': "The preview stopped working. Your writing isn't affected.",
  'preview.retry': 'Restart the preview',
  'preview.turns': 'Turn the pages',
  'preview.previous': 'Back',
  'preview.next': 'Next',
  'preview.previousChapter': 'Previous chapter',
  'preview.nextChapter': 'Next chapter',
  'preview.chapters': 'Move between chapters',
  'preview.onePage': 'One page',
  'preview.twoPages': 'Two pages',
  'preview.pagesShown': 'Pages shown',
  'preview.enlarge': 'Enlarge…',
  'preview.enlargePage': 'Enlarge {page}',
  'preview.closeUp': 'Close-up of {page}',
  'preview.closeUpFailed': "This page couldn't be enlarged. Close this and try again.",
  'preview.previousPages': 'Previous pages',
  'preview.nextPages': 'Next pages',
  'preview.previousPage': 'Previous page',
  'preview.nextPage': 'Next page',
  'preview.backToCursor': 'Back to the cursor',
  'preview.chapterPage': 'Chapter {chapter}, page {number}',
  'preview.chapterPageAbout': 'Chapter {chapter}, about page {number}',
  'preview.blank': 'Blank page',
  'preview.pagePending': 'Setting this page…',
  'preview.spread': '{left}; {right}',

  'toolbar.exportPdf': 'Export PDF',
  'export.label': 'PDF export',
  'export.reading': 'Reading the book…',
  'export.progress': 'Making the PDF: {percent}%',
  'export.finishing': 'Putting the PDF together…',
  'export.cancel': 'Cancel',
  'export.ready': 'The PDF is ready.',
  'export.save': 'Save the PDF',
  'export.unreadable': "A chapter couldn't be read, so the PDF wasn't made. Your writing isn't affected.",
  'export.failed': "The PDF couldn't be made. Your writing isn't affected.",
  'export.retry': 'Try again',
  'export.dismiss': 'Dismiss',
  'export.making': 'Making the PDF…',
  'toolbar.exportEpub': 'Export EPUB',
  'epub.label': 'EPUB export',
  'epub.reading': 'Reading the book…',
  'epub.progress': 'Making the EPUB: {percent}%',
  'epub.finishing': 'Putting the EPUB together…',
  'epub.cancel': 'Cancel',
  'epub.ready': 'The EPUB is ready.',
  'epub.missing': {
    one: "{count} picture couldn't be added, so its description shows instead.",
    other: "{count} pictures couldn't be added, so their descriptions show instead.",
  },
  'epub.save': 'Save the EPUB',
  'epub.unreadable': "A chapter couldn't be read, so the EPUB wasn't made. Your writing isn't affected.",
  'epub.failed': "The EPUB couldn't be made. Your writing isn't affected.",
  'epub.retry': 'Try again',
  'epub.dismiss': 'Dismiss',
  'epub.making': 'Making the EPUB…',
  'toolbar.exportDocx': 'Export DOCX…',
  'docx.title': 'Export a Word file',
  'docx.look': 'How it looks',
  'docx.theme': 'Book theme, as the PDF looks',
  'docx.times': 'Manuscript in Times New Roman',
  'docx.courier': 'Manuscript in Courier New',
  'docx.manuscriptHint': 'A manuscript is set as agents and editors expect: 12 pt, double-spaced, with your surname, the title and the page number at the top of each page.',
  'docx.export': 'Export',
  'docx.close': 'Cancel',
  'docx.label': 'DOCX export',
  'docx.reading': 'Reading the book…',
  'docx.progress': 'Making the DOCX: {percent}%',
  'docx.finishing': 'Putting the DOCX together…',
  'docx.cancel': 'Cancel',
  'docx.ready': 'The DOCX is ready.',
  'docx.missing': {
    one: "{count} picture couldn't be added, so its description shows instead.",
    other: "{count} pictures couldn't be added, so their descriptions show instead.",
  },
  'docx.notKept': "A copy of the chapters as exported couldn't be kept on this device.",
  'docx.save': 'Save the DOCX',
  'docx.unreadable': "A chapter couldn't be read, so the DOCX wasn't made. Your writing isn't affected.",
  'docx.failed': "The DOCX couldn't be made. Your writing isn't affected.",
  'docx.retry': 'Try again',
  'docx.dismiss': 'Dismiss',
  'docx.making': 'Making the DOCX…',
  'toolbar.exportBundle': 'Export bundle',
  'toolbar.find': 'Find',
  'bundle.label': 'Markdown bundle',
  'bundle.saving': 'Saving your latest typing…',
  'bundle.progress': 'Making the bundle: {percent}%',
  'bundle.finishing': 'Putting the bundle together…',
  'bundle.ready': "The bundle is ready: every chapter, the trash, the history, the images, the notes and the book's settings.",
  'bundle.readyExceptions': 'The bundle is ready, with exceptions:',
  'bundle.unreadable': {
    one: "{count} record on this device couldn't be read. It's in the bundle as it was stored, with any words it holds.",
    other: "{count} records on this device couldn't be read. They're in the bundle as they were stored, with any words they hold.",
  },
  'bundle.unsaved': "Your latest typing couldn't be saved, so it isn't in the bundle.",
  'bundle.tooManyFiles': "Its history is so deep that it holds more files than Import bundle… reads at once ({count}), so it can't be brought back whole. Every chapter is in it as a Markdown file.",
  'bundle.save': 'Save the bundle',
  'bundle.making': 'Making the bundle…',
  'bundle.outdated': 'The app was updated in another tab. Reload this page, then export again.',
  'bundle.tooLarge': "The bundle would be larger than 4 GB, so it wasn't made. Your writing isn't affected.",
  'bundle.failed': "The bundle couldn't be made. Your writing isn't affected.",
  'backup.label': 'Backups',
  'backup.reminderLabel': 'Backup reminder',
  'backup.none': 'No backup folder chosen.',
  'backup.folder': 'Backing up to the folder “{name}”.',
  'backup.how': "Once a day while the app is open, each book changed since its last backup is saved in the folder as a bundle. The last 14 of each book are kept; the app never deletes a file it didn't write.",
  'backup.unsupported': "This browser can't keep a backup folder. After 7 days of changes without a backup, the app reminds you to save one; Export bundle saves one any time.",
  'backup.plain': 'Backups are plain files, not encrypted: anyone who can open them can read your books. On an iPhone or iPad, a saved or shared file may go to iCloud.',
  'backup.choose': 'Choose a folder…',
  'backup.change': 'Change folder…',
  'backup.now': 'Back up now',
  'backup.running': 'Backing up…',
  'backup.forget': 'Stop using this folder',
  'backup.never': 'Not backed up yet',
  'backup.today': 'Last backup today',
  'backup.age': {
    one: 'Last backup {count} day ago',
    other: 'Last backup {count} days ago',
  },
  'backup.bookAge': '{title}: {age}',
  'backup.paused': 'Backups are paused: the browser asks again before the app may write to the backup folder.',
  'backup.allow': 'Allow backups',
  'backup.refused': 'Backups stopped: the app may not write to the backup folder. Choose it again, or another.',
  'backup.gone': 'Backups stopped: the backup folder is gone. Choose another.',
  'backup.full': "Backups stopped: the backup folder's disk is full. Make room, then try again.",
  'backup.failed': "The backup couldn't be made. Your writing isn't affected.",
  'backup.books': {
    one: "{count} book couldn't be backed up: {titles}. Your writing isn't affected.",
    other: "{count} books couldn't be backed up: {titles}. Your writing isn't affected.",
  },
  'backup.lost': 'Backups stopped: choose the backup folder again.',
  'backup.outdated': 'The app was updated in another tab. Reload this page to back up.',
  'backup.later': 'Later',
  'backup.reminder': '“{title}” has changes that aren’t backed up. Save a backup outside this browser.',
  'backup.reminderOthers': {
    one: '{count} more book is waiting too.',
    other: '{count} more books are waiting too.',
  },
  'backup.share': 'Share the backup',
  'backup.download': 'Save the backup',
  // Part 41b: the backup password (cockatrice-ui `BackupSync.jsx`'s row, `SyncSetup.jsx`'s check; tables E and BW).
  'backup.lockPaused': 'No backups are written until the backup password is set again: none as plain files in its place.',
  'backup.locked': 'Backups are locked with your backup password: nobody can open them without it, not even us. A bundle saved with Export is a plain file.',
  'lock.label': 'Backup password',
  'lock.help': 'Optionally lock backup files with a password of your own.',
  'lock.helpOn': "Backups are locked with your password. It is checked on this device and never sent to us. Without it they can't be opened, even by us.",
  'lock.unreadable': "Backups are paused: the backup password kept on this device can't be read. Set the password again to go on.",
  'lock.set': 'Set password',
  'lock.check': 'Check',
  'lock.change': 'Change…',
  'lock.off': 'Turn off',
  // The openers of Turn off's confirmation: an ellipsis, as a dialog follows (DESIGN.md; the design's "Turn off…").
  'lock.offOpen': 'Turn off…',
  'lock.sheet': 'Backup password',
  'lock.setTitle': 'Set a backup password',
  'lock.changeTitle': 'Change the backup password',
  'lock.offTitle': 'Turn off the backup password?',
  'lock.checkTitle': 'Do you still know your backup password?',
  'lock.setLead': 'Backups made from now on are locked with this password. It stays on this device and is never sent to us.',
  'lock.changeLead': 'Backups made from now on are locked with the new password. Backups already made keep the password they were made with, so keep the old one for them.',
  'lock.field': 'Backup password',
  'lock.repeat': 'Type it again',
  'lock.understood': "I understand a forgotten backup password can't be recovered, and backups locked with it can't be opened",
  'lock.setSubmit': 'Set password',
  'lock.changeSubmit': 'Change password',
  'lock.setting': 'Setting the password…',
  'lock.error.repeat': "The two passwords aren't the same.",
  'lock.error.understood': 'Tick that you understand first.',
  'lock.error.set': "The backup password couldn't be set. Your backups are as they were.",
  'lock.setDone': 'Backups made from now on are locked with your backup password.',
  'lock.changeDone': 'Backups made from now on are locked with the new password. Older ones still open with the old one.',
  'lock.offLead': 'Backups are plain files again from the next one: anyone who can open them can read your books. Backups already locked still need the password.',
  'lock.error.off': "The backup password couldn't be turned off. Backups stay locked.",
  'lock.offDone': 'Backups are plain files again from the next one.',
  'lock.checkLead': 'Now and then we ask, so you find out early if it has gone missing. Type your backup password to check.',
  'lock.checkHint': 'The password is checked on this device. It is never sent to us.',
  'lock.later': 'Remind me later',
  'lock.lost': 'I can’t find it',
  'lock.wrong': "That isn't it.",
  'lock.wrongWays': 'Forgotten it? Set a new one: backups from then on use it, and backups already made still need the old one. Or turn the password off.',
  'lock.newPassword': 'Set a new password',
  'lock.error.check': "The password couldn't be checked. Try again.",
  'lock.right': "That's the one. You'll be asked again now and then.",
  'lock.notice': 'Do you still know your backup password?',
  'lock.noticeLabel': 'Backup password check',
  'lock.unlocked': {
    one: "{count} book wasn't backed up: {titles}. Its locked backup couldn't be checked, so nothing was written. Your writing isn't affected.",
    other: "{count} books weren't backed up: {titles}. Their locked backups couldn't be checked, so nothing was written. Your writing isn't affected.",
  },

  'book.import': 'Import…',
  'import.label': 'Import',
  'import.reading': 'Reading “{name}”: {percent}%',
  'import.readingNews': 'Reading the file…',
  'import.adding': 'Adding pictures: {done} of {total}',
  'import.addingNews': 'Adding pictures…',
  'import.storing': 'Storing the book…',
  'import.done': {
    one: 'Imported “{title}”: {count} chapter. It is open now.',
    other: 'Imported “{title}”: {count} chapters. It is open now.',
  },
  'import.missing': {
    one: "{count} picture couldn't be added; it shows as an empty frame.",
    other: "{count} pictures couldn't be added; they show as empty frames.",
  },
  'import.notSupported': 'Only Word files (.docx) and Markdown or text files (.md, .txt) can be imported.',
  'import.tooLarge': "The file is larger than 50 MB, so it wasn't imported.",
  'import.notZip': "This isn't a Word file, so it wasn't imported.",
  'import.notDocx': "This isn't a Word document, so it wasn't imported.",
  'import.damaged': "The file is damaged, so it wasn't imported.",
  'import.unsupported': "The file is encrypted or saved in a way the app can't read, so it wasn't imported.",
  'import.tooBig': "The file holds more than the app can import at once, so it wasn't imported.",
  'import.unreadable': "The file couldn't be read. It may have been moved or changed since you picked it.",
  'import.empty': "The file holds no text, so there was nothing to import.",
  'import.expands': "The file would make a book far larger than itself, so it wasn't imported.",
  'import.ownExport': "This Word file was exported from Cockatrice. To bring an editor's changes back into its book, open the book and use Re-import DOCX…; to move a book, use the bundle.",
  'import.timedOut': "Reading the file took too long, so it was stopped. Nothing was imported.",
  'import.busy': 'Another file is being imported. Try again when it is done.',
  'import.notEditable': "The book can't be changed in this tab just now, so nothing was imported.",
  'import.saveFailed': "The open chapter couldn't be saved, so nothing was imported. Your text is still here.",
  'import.stale': 'Another tab took over the editing, so nothing was imported.',
  'import.writeFailed': "The book couldn't be stored on this device, so nothing was imported.",
  'import.failed': "The file couldn't be imported. Your writing isn't affected.",
  'import.reviewTitle': 'Import a book',
  'import.summary': {
    one: '{count} chapter, {words} words.',
    other: '{count} chapters, {words} words.',
  },
  'import.newBook': 'It becomes a new book. Nothing in your other books changes.',
  'import.bookTitle': 'Book title',
  'import.chapters': 'Chapters',
  'import.words': {
    one: '{count} word',
    other: '{count} words',
  },
  'import.long': 'Over {words} words: long chapters make the editor slower. Once imported, it offers Split it… at its headings or scene breaks.',
  'import.pictures': {
    one: '{count} picture will be added.',
    other: '{count} pictures will be added.',
  },
  'import.check': 'Check after importing',
  'import.where': 'In chapters {list}.',
  'import.confirm': 'Import book',
  'import.cancel': 'Cancel',
  'book.importBundle': 'Import bundle…',
  'bundleImport.label': 'Bundle import',
  'bundleImport.checking': 'Checking “{name}”: {percent}%',
  'bundleImport.checkingNews': 'Checking the bundle…',
  'bundleImport.storing': 'Importing the book: {percent}%',
  'bundleImport.storingNews': 'Importing the book…',
  'bundleImport.finishing': 'Putting the book together…',
  'bundleImport.undoing': 'Removing what was imported…',
  'bundleImport.cancelled': 'The import was cancelled. Nothing of it was kept.',
  'bundleImport.done': {
    one: 'Imported “{title}”: {count} chapter. It is on My books.',
    other: 'Imported “{title}”: {count} chapters. It is on My books.',
  },
  'bundleImport.doneCopy': {
    one: 'Imported a copy of “{title}”: {count} chapter. It is on My books.',
    other: 'Imported a copy of “{title}”: {count} chapters. It is on My books.',
  },
  'bundleImport.open': 'Open the book',
  'bundleImport.missing': {
    one: "{count} picture couldn't be imported; it shows as an empty frame.",
    other: "{count} pictures couldn't be imported; they show as empty frames.",
  },
  'bundleImport.recovered': {
    one: "{count} damaged record was recovered: its words are at the end of the book or among the notes.",
    other: "{count} damaged records were recovered: their words are at the end of the book or among the notes.",
  },
  'bundleImport.notBundle': "This isn't a Cockatrice bundle, so it wasn't imported.",
  'bundleImport.newer': 'The bundle was made by a newer version of Cockatrice. Reload this page to update the app, then import it again.',
  'bundleImport.damaged': "The bundle is damaged, so it wasn't imported.",
  'bundleImport.fileChanged': "The bundle changed while it was imported, so nothing was imported. Import it again.",
  'bundleImport.tooLarge': "The file is larger than 4 GB, so it wasn't imported.",
  'bundleImport.exists': 'This book appeared on this device while you reviewed the bundle, so nothing was imported. You can import it as a copy.',
  'bundleImport.taken': "Some of the bundle's records have the same IDs as records already on this device, so it wasn't imported as it is. You can import it as a copy, with new IDs.",
  'bundleImport.readFailed': "The books on this device couldn't be read, so nothing was imported.",
  'bundleImport.failed': "The bundle couldn't be imported. Your writing isn't affected.",
  'bundleImport.copy': 'Import as a copy…',
  'bundleImport.reviewTitle': 'Import a bundle',
  'bundleImport.summary': {
    one: 'The bundle holds {count} chapter.',
    other: 'The bundle holds {count} chapters.',
  },
  'bundleImport.trash': { one: '{count} chapter in the trash', other: '{count} chapters in the trash' },
  'bundleImport.copies': { one: '{count} conflict copy', other: '{count} conflict copies' },
  'bundleImport.snapshots': { one: '{count} snapshot in the history', other: '{count} snapshots in the history' },
  'bundleImport.pictures': { one: '{count} picture', other: '{count} pictures' },
  'bundleImport.notes': { one: '{count} note', other: '{count} notes' },
  'bundleImport.comments': { one: '{count} Word comment', other: '{count} Word comments' },
  'bundleImport.also': 'It also holds:',
  'bundleImport.noPictures': {
    one: "{count} picture has no file in the bundle; it will show as an empty frame.",
    other: "{count} pictures have no file in the bundle; they will show as empty frames.",
  },
  'bundleImport.damagedRecords': {
    one: "{count} record in it couldn't be read where it was made; its words come back at the end of the book or among the notes.",
    other: "{count} records in it couldn't be read where they were made; their words come back at the end of the book or among the notes.",
  },
  'bundleImport.newBook': 'It becomes a new book, as it was when the bundle was made. Nothing in your other books changes.',
  'bundleImport.here': 'This book is already on this device, so the bundle is imported as a copy: a new book beside it. Nothing in the book here changes.',
  'bundleImport.meanwhile': 'This book came onto this device while you reviewed the bundle, so it is imported as a copy: a new book beside it. Nothing in the book here changes.',
  'bundleImport.clash': "Some of the bundle's records have the same IDs as records already on this device, so it is imported as a copy with new IDs: a new book. Nothing else on this device changes.",
  'bundleImport.ready': '“{name}” is checked and ready to review.',
  'bundleImport.review': 'Review the bundle',
  'bundleImport.bookTitle': 'Book title',
  'bundleImport.confirm': 'Import book',
  'bundleImport.confirmCopy': 'Import as a copy',
  'bundleImport.cancel': 'Cancel',
  'bundleImport.recoveredText': 'Recovered text',
  'bundleImport.recoveredPictures': 'Recovered pictures',
  'bundleImport.picture': 'Recovered picture {n}',
  'bundleImport.update': 'Update this book…',
  'bundleImport.updating': 'Reading the bundle for the update: {percent}%',
  'bundleImport.updatingNews': 'Reading the bundle for the update…',
  'bundleImport.planning': 'Comparing the bundle with the book…',
  'bundleImport.updateReady': 'The update of “{title}” is ready to review. Nothing in the book changes until you apply it.',
  'bundleImport.updateChanged': 'The book changed since you reviewed the update, so nothing was changed. The update is ready to review again.',
  'bundleImport.updateSaveFailed': "The open chapter couldn't be saved, so nothing was changed. The update is ready to review again.",
  'bundleImport.updateNotEditable': "The book can't be changed in this tab now, so nothing was changed. The update is ready to review again.",
  'bundleImport.reviewUpdate': 'Review the update',
  'bundleImport.applying': 'Updating the book…',
  'bundleImport.updated': {
    one: '“{title}” is updated: {count} chapter changed.',
    other: '“{title}” is updated: {count} chapters changed.',
  },
  'bundleImport.updatedCopies': {
    one: 'Your earlier text of {count} of them is kept as a conflict copy.',
    other: 'Your earlier text of {count} of them is kept as conflict copies.',
  },
  'bundleImport.updatedAdded': { one: '{count} new chapter was added.', other: '{count} new chapters were added.' },
  'bundleImport.gone': "The book isn't on this device any more, so nothing was updated.",
  'bundleImport.updateHeading': 'Update “{title}”',
  'bundleImport.updateIntro': 'Before a chapter changes, its text is kept in its history, and a note or detail you take keeps yours below it. Nothing of the book is deleted.',
  'bundleImport.updTaken': { one: "{count} chapter takes the bundle's text:", other: "{count} chapters take the bundle's text:" },
  'bundleImport.updMerged': {
    one: '{count} chapter changed in both is merged; your text is kept as a conflict copy:',
    other: '{count} chapters changed in both are merged; your text is kept as conflict copies:',
  },
  'bundleImport.updConflicts': {
    one: '{title}: {count} passage changed in both, the bundle’s taken',
    other: '{title}: {count} passages changed in both, the bundle’s taken',
  },
  'bundleImport.updCopied': {
    one: "{count} chapter with no shared history takes the bundle's text; your text is kept as a conflict copy:",
    other: "{count} chapters with no shared history take the bundle's text; your text is kept as conflict copies:",
  },
  'bundleImport.updNewer': { one: '{count} chapter is newer on this device and is kept.', other: '{count} chapters are newer on this device and are kept.' },
  'bundleImport.updSame': { one: '{count} chapter is the same in both.', other: '{count} chapters are the same in both.' },
  'bundleImport.updInHistory': {
    one: "{count} chapter has changes in the bundle the merge doesn't write (such as a note nothing cites); it stays as it is here.",
    other: "{count} chapters have changes in the bundle the merge doesn't write (such as a note nothing cites); they stay as they are here.",
  },
  'bundleImport.updKept': {
    one: "The bundle's text of {count} chapter it doesn't take whole is kept in that chapter's history.",
    other: "The bundle's texts of {count} chapters it doesn't take whole are kept in their history.",
  },
  'bundleImport.updNoText': { one: "{count} chapter's text isn't in the bundle; it is kept.", other: "{count} chapters' texts aren't in the bundle; they are kept." },
  'bundleImport.updUnreadable': { one: "{count} chapter can't be read on this device; it is kept as it is.", other: "{count} chapters can't be read on this device; they are kept as they are." },
  'bundleImport.updGone': {
    one: '{count} chapter was deleted here since the update began; the bundle’s text of it is kept under “Needs your attention”.',
    other: '{count} chapters were deleted here since the update began; the bundle’s texts of them are kept under “Needs your attention”.',
  },
  'bundleImport.updAdded': { one: '{count} new chapter is added:', other: '{count} new chapters are added:' },
  'bundleImport.updOnlyHere': { one: '{count} chapter only on this device is kept.', other: '{count} chapters only on this device are kept.' },
  'bundleImport.updAlso': 'From its history and notes it adds:',
  'bundleImport.updOffers': 'The bundle has other words for these. Tick what to take; nothing is taken otherwise:',
  'bundleImport.offerTitle': 'Rename “{chapter}” to “{value}”',
  'bundleImport.offerSynopsis': 'Take the bundle’s synopsis of “{chapter}”, yours kept below it:',
  'bundleImport.offerNotes': 'Take the bundle’s note on “{chapter}”, yours kept below it:',
  'bundleImport.offerNote': 'Take the bundle’s note “{name}”, your text kept below it:',
  'bundleImport.offerNoteUntitled': 'Take the bundle’s untitled note, your text kept below it:',
  'bundleImport.offerNoteRenamed': 'Take the bundle’s note “{name}”, named “{renamed}” there, your text kept below it:',
  'bundleImport.offerDetail': 'Take the bundle’s {field}: “{value}”',
  'bundleImport.offerDetailFree': 'Take the bundle’s {field}, yours kept below it:',
  'bundleImport.offerCover': 'Take the bundle’s cover picture',
  'bundleImport.offerCoverColour': 'Take the bundle’s cover colour ({colour})',
  'bundleImport.field.title': 'book title',
  'bundleImport.field.subtitle': 'subtitle',
  'bundleImport.field.author': 'author',
  'bundleImport.field.publisher': 'publisher',
  'bundleImport.field.copyrightHolder': 'copyright holder',
  'bundleImport.field.copyrightYear': 'copyright year',
  'bundleImport.field.isbn': 'ISBN',
  'bundleImport.field.language': 'language',
  'bundleImport.field.cover': 'cover picture',
  'bundleImport.field.coverColour': 'cover colour',
  'bundleImport.field.dedication': 'dedication',
  'bundleImport.field.epigraph': 'epigraph',
  'bundleImport.field.epigraphSource': 'epigraph',
  'bundleImport.field.acknowledgements': 'acknowledgements',
  'bundleImport.field.aboutAuthor': '“About the author”',
  'bundleImport.field.alsoBy': '“Also by”',
  'bundleImport.offerSubtitle': 'Take the bundle’s subtitle of “{chapter}”: {value}',
  'bundleImport.offerTheme': 'Take the bundle’s look for the book',
  'bundleImport.apply': 'Apply the update',
  // Part 41b, E11–E14: a locked backup picked for import.
  'bundleImport.lockedTitle': 'Open a locked backup',
  'bundleImport.lockedLead': '“{name}” is locked with a backup password. Type it to open the file.',
  'bundleImport.unlock': 'Open',
  'bundleImport.opening': 'Opening…',
  'bundleImport.wrongPassword': "That password doesn't open this file.",
  'bundleImport.lockedNewer': 'The backup was made by a newer version of Cockatrice. Reload this page to update the app, then import it again.',
  'bundleImport.lockedDamaged': "The backup is damaged and can't be read, so it wasn't imported.",
  'bundleImport.notBackup': "This isn't a Cockatrice backup, so it wasn't imported.",
  'bundleImport.openFailed': "The backup couldn't be opened. Try again.",
  'book.reimport': 'Re-import DOCX…',
  'reimport.label': 'Re-import',
  'reimport.reading': 'Reading “{name}”: {percent}%',
  'reimport.readingNews': 'Reading the Word file…',
  'reimport.comparing': 'Comparing pictures: {done} of {total}',
  'reimport.comparingNews': 'Comparing pictures…',
  'reimport.merging': 'Comparing chapters: {done} of {total}',
  'reimport.mergingNews': 'Comparing chapters…',
  'reimport.adding': 'Adding pictures: {done} of {total}',
  'reimport.addingNews': 'Adding pictures…',
  'reimport.storing': 'Storing the changes…',
  'reimport.done': { one: 'The Word file was taken into the book: {count} chapter changed.', other: 'The Word file was taken into the book: {count} chapters changed.' },
  'reimport.newChapters': { one: '{count} new chapter added.', other: '{count} new chapters added.' },
  'reimport.copies': { one: 'Your text before is kept as a conflict copy of {count} chapter, under “Needs your attention”.', other: 'Your text before is kept as a conflict copy of {count} chapters, under “Needs your attention”.' },
  'reimport.missingPictures': { one: "{count} picture couldn't be added; it shows as an empty frame.", other: "{count} pictures couldn't be added; they show as empty frames." },
  'reimport.notSupported': 'Only a Word file (.docx) can be re-imported.',
  'reimport.unreadable': "The open book couldn't be read whole, so nothing was re-imported. Your writing isn't affected.",
  'reimport.mergeFailed': "The file's chapters couldn't be compared with the book's, so nothing was re-imported. Your writing isn't affected.",
  'reimport.bookChanged': 'Another book was opened while the Word file was being re-imported, so nothing was written. Open the book again and re-import the file.',
  'reimport.changed': 'The book changed while you reviewed the file, so nothing was written. Re-import it again to see what it changes now.',
  'reimport.notEditable': "The book can't be changed in this tab just now, so nothing was re-imported.",
  'reimport.saveFailed': "The open chapter couldn't be saved, so nothing was re-imported. Your text is still here.",
  'reimport.stale': 'Another tab took over the editing, so nothing was re-imported.',
  'reimport.writeFailed': "The changes couldn't be stored on this device, so nothing was re-imported.",
  'reimport.failed': "The file couldn't be re-imported. Your writing isn't affected.",
  'reimport.reviewTitle': 'Re-import “{name}”',
  'reimport.summary': { one: 'The file holds {count} chapter.', other: 'The file holds {count} chapters.' },
  'reimport.intoBook': "A chapter you take gets the file's text in this book; a copy of it is kept in its history first. Nothing changes until you press Apply.",
  'reimport.notRead': "The title page, front and back matter and chapter numbers aren't read from the file.",
  'reimport.chapters': 'Chapters',
  'reimport.take': 'Take “{title}”',
  'reimport.words': { one: '{count} word', other: '{count} words' },
  'reimport.state.unchanged': 'No changes in the file.',
  'reimport.state.here': 'Changed here since the export; the file has no changes to it.',
  'reimport.state.changed': 'Changed in the file.',
  'reimport.state.both': 'Changed here and in the file. Taking it keeps your text now as a conflict copy.',
  'reimport.state.new': 'New in the file: taking it adds it after the chapter before it.',
  'reimport.noBase': 'The copy kept when it was exported is gone, so the file is compared with your text now. Taking it keeps your text now as a conflict copy.',
  'reimport.conflicts': { one: "{count} place was changed both here and in the file; the file's text is taken there.", other: "{count} places were changed both here and in the file; the file's text is taken there." },
  'reimport.subtitle': 'Take the file’s subtitle: “{subtitle}”',
  'reimport.rename': 'Rename it “{title}”, as the file titles it',
  'reimport.diff': 'What taking it changes',
  'reimport.removed': 'Removed:',
  'reimport.addedText': 'Added:',
  'reimport.blank': '(a scene break or an empty line)',
  'reimport.more': { one: 'And {count} more change.', other: 'And {count} more changes.' },
  'reimport.missingHeading': 'Not in the file',
  'reimport.missingBody': 'These chapters stay as they are.',
  'reimport.pictures': { one: '{count} picture is new or was replaced; it is added to the book with the chapter that shows it.', other: '{count} pictures are new or were replaced; each is added to the book with the chapter that shows it.' },
  'reimport.comments': {
    one: "{count} Word comment is kept with its chapter when you press Apply, with its author's name. The app doesn't show comments yet; the Markdown bundle keeps them.",
    other: "{count} Word comments are kept with their chapters when you press Apply, each with its author's name. The app doesn't show comments yet; the Markdown bundle keeps them.",
  },
  'reimport.commentsInNew': { one: '{count} of them is in a new chapter, and is kept only if you take it.', other: '{count} of them are in new chapters, and are kept only with the chapters you take.' },
  'reimport.commentsKept': { one: '{count} Word comment kept.', other: '{count} Word comments kept.' },
  'reimport.check': 'Check after re-importing',
  'reimport.apply': 'Apply',
  'reimport.cancel': 'Cancel',
  'import.flag.table': { one: '{count} table became plain paragraphs.', other: '{count} tables became plain paragraphs.' },
  'import.flag.tracked': { one: 'Tracked changes were accepted ({count} place).', other: 'Tracked changes were accepted ({count} places).' },
  'import.flag.comments': { one: '{count} comment was left out.', other: '{count} comments were left out.' },
  'import.flag.hidden': { one: '{count} piece of hidden text was left out.', other: '{count} pieces of hidden text were left out.' },
  'import.flag.underline': { one: 'Underlining was dropped ({count} place).', other: 'Underlining was dropped ({count} places).' },
  'import.flag.strike': { one: 'Strikethrough was dropped ({count} place).', other: 'Strikethrough was dropped ({count} places).' },
  'import.flag.superscript': { one: 'Superscript was dropped ({count} place).', other: 'Superscript was dropped ({count} places).' },
  'import.flag.subscript': { one: 'Subscript was dropped ({count} place).', other: 'Subscript was dropped ({count} places).' },
  'import.flag.caps': { one: 'All-caps formatting was dropped ({count} place); the letters are as typed.', other: 'All-caps formatting was dropped ({count} places); the letters are as typed.' },
  'import.flag.link': { one: "{count} link to something other than a web page or email address became plain text.", other: "{count} links to something other than a web page or email address became plain text." },
  'import.flag.contents': { one: 'A table of contents or index was left out.', other: '{count} tables of contents or indexes were left out.' },
  'import.flag.embedded': { one: '{count} embedded object was left out.', other: '{count} embedded objects were left out.' },
  'import.flag.macros': "The file holds macros. They were not imported and never run.",
  'import.flag.graphic': { one: '{count} chart, diagram or shape was left out.', other: '{count} charts, diagrams or shapes were left out.' },
  'import.flag.equation': { one: '{count} equation was left out.', other: '{count} equations were left out.' },
  'import.flag.symbol': { one: '{count} symbol from a symbol font was left out.', other: '{count} symbols from symbol fonts were left out.' },
  'import.flag.textbox': { one: "{count} text box's text was put after its paragraph.", other: "{count} text boxes' text was put after their paragraphs." },
  'import.flag.endnotes': { one: '{count} endnote became a footnote.', other: '{count} endnotes became footnotes.' },
  'import.flag.missing-note': { one: '{count} note reference had no note and was left out.', other: '{count} note references had no note and were left out.' },
  'import.flag.deep-heading': { one: '{count} heading deeper than level 6 became level 6.', other: '{count} headings deeper than level 6 became level 6.' },
  'import.flag.linked-image': { one: "{count} picture linked from outside the file wasn't fetched.", other: "{count} pictures linked from outside the file weren't fetched." },
  'import.flag.image-format': { one: '{count} picture in a format other than JPEG, PNG or WebP was left out.', other: '{count} pictures in formats other than JPEG, PNG or WebP were left out.' },
  'import.flag.image-large': { one: '{count} picture larger than 50 MB was left out.', other: '{count} pictures larger than 50 MB were left out.' },
  'import.flag.image-missing': { one: "{count} picture the file names isn't in it and was left out.", other: "{count} pictures the file names aren't in it and were left out." },
  'import.flag.image-count': { one: '{count} picture past the first 1000 was left out.', other: '{count} pictures past the first 1000 were left out.' },
  'import.flag.not-split': "The book couldn't be split at its headings, so it is one chapter. You can split it later.",
  'import.flag.structure': { one: "{count} chapter's lists, quotes and notes couldn't be kept; its text is plain paragraphs.", other: "{count} chapters' lists, quotes and notes couldn't be kept; their text is plain paragraphs." },
  'import.flag.formatting-kept-out': { one: "{count} paragraph's italics or bold couldn't be kept.", other: "{count} paragraphs' italics or bold couldn't be kept." },
  'import.flag.encoding': "The file isn't UTF-8 text, so it was read as Western (Windows-1252). Check its accented letters.",
  'import.flag.code': { one: '{count} code fence was kept as text.', other: '{count} code fences were kept as text.' },
  'import.flag.html': { one: '{count} piece of HTML was kept as text.', other: '{count} pieces of HTML were kept as text.' },
  'import.flag.indented': { one: '{count} indented paragraph joined the note or list before it.', other: '{count} indented paragraphs joined the note or list before them.' },
  'import.flag.outside-image': { one: "{count} picture points outside the file; it shows as an empty frame.", other: "{count} pictures point outside the file; they show as empty frames." },
  'import.flag.pandoc': { one: "{count} piece of pandoc’s own markup (a span, attributes or a div fence) was taken out; its text is kept.", other: "{count} pieces of pandoc’s own markup (spans, attributes, div fences) were taken out; their text is kept." },

  'toolbar.insertImage': 'Insert image',
  'images.label': 'Images',
  'images.untitled': 'Image',
  'images.working': 'Putting in {name}…',
  'images.workingOf': 'Putting in image {number} of {total}: {name}…',
  'images.putIn': {
    one: 'The image is in the text.',
    other: '{count} images are in the text.',
  },
  'images.notAll': "Not every image went in:",
  'images.unsupported': "{name}: not a picture the app takes. Use a JPEG, PNG or WebP file.",
  'images.damaged': "{name}: the picture couldn't be read. The file may be damaged.",
  'images.tooLarge': '{name}: the file is larger than 50 MB.',
  'images.tooBig': '{name}: the picture is larger than 16,384 pixels a side or 50 megapixels.',
  'images.full': "{name}: this book's images would pass 100 MB, so it wasn't added.",
  'images.failed': "{name}: it couldn't be put in. Your writing isn't affected.",
  'images.notPutIn': "{name}: the chapter closed before the picture was ready, so it wasn't put in.",

  // Part 19a: find and replace (decision 41).
  'find.label': 'Find and replace',
  'find.query': 'Find',
  'find.replacement': 'Replace with',
  'find.matchCase': 'Match case',
  'find.wholeWords': 'Whole words',
  'find.regex': 'Regular expression',
  'find.scope': 'Search in',
  'find.scope.chapter': 'This chapter',
  'find.scope.book': 'Whole book',
  'find.previous': 'Previous',
  'find.next': 'Next',
  'find.replace': 'Replace',
  'find.replaceAll': 'Replace all',
  'find.undo': 'Undo replace all',
  'find.close': 'Close find',
  'find.more': 'Options and replace',
  'find.searching': 'Searching…',
  'find.none': 'No matches.',
  'find.matches': { one: '{count} match.', other: '{count} matches.' },
  'find.matchesIn': { one: '{count} match in {chapters} chapters.', other: '{count} matches in {chapters} chapters.' },
  'find.matchesInOne': { one: '{count} match in 1 chapter.', other: '{count} matches in 1 chapter.' },
  'find.position': 'Match {index} of {of} in this chapter.',
  'find.unreadable': { one: "{count} chapter couldn't be read and wasn't searched.", other: "{count} chapters couldn't be read and weren't searched." },
  'find.invalid': "That isn't a pattern the search understands. Check the brackets and backslashes, or turn off Regular expression.",
  'find.tooLong': 'The search or the replacement is longer than 1,000 characters.',
  'find.stopped': 'The pattern took too long and was stopped. Try a simpler one.',
  'find.failed': "The search didn't work. Try again.",
  'find.replacing': 'Replacing…',
  'find.replaced': {
    one: 'Replaced {count} match in {chapters} chapters. Undo replace all puts it back.',
    other: 'Replaced {count} matches in {chapters} chapters. Undo replace all puts them back.',
  },
  'find.replacedOne': {
    one: 'Replaced {count} match in 1 chapter. Undo replace all puts it back.',
    other: 'Replaced {count} matches in 1 chapter. Undo replace all puts them back.',
  },
  'find.nothingReplaced': 'Nothing was replaced.',
  'find.left': {
    one: '{count} match was left as it was: it crosses a line break, a picture or a note.',
    other: '{count} matches were left as they were: they cross a line break, a picture or a note.',
  },
  'find.leftOne': "That match wasn't replaced: it crosses a line break, a picture or a note.",
  'find.changed': 'A chapter changed while replacing, so nothing was replaced. Try again.',
  'find.replaceFailed': "Replacing didn't work; nothing was changed.",
  'find.undone': {
    one: 'Replace all undone in {count} chapter.',
    other: 'Replace all undone in {count} chapters.',
  },
  'find.kept': {
    one: '{count} chapter you changed since kept its text.',
    other: '{count} chapters you changed since kept their text.',
  },
  'find.undoFailed': "Undo didn't work; nothing was changed.",

  'words.button': 'Word count',
  'words.chapter': { one: '{count} word', other: '{count} words' },
  'words.today': '{today} of {goal} today',
  'words.title': 'Word count',
  'words.thisChapter': 'This chapter',
  'words.selection': 'Selected text',
  'words.book': 'Whole book',
  'words.todayLabel': 'Today, on this device',
  'words.count': { one: '{count} word', other: '{count} words' },
  'words.counting': 'Counting…',
  'words.failed': "The words couldn't be counted. Try again.",
  'words.unreadable': { one: "{count} chapter couldn't be read and wasn't counted.", other: "{count} chapters couldn't be read and weren't counted." },
  'words.counted': { one: 'The book has {count} word.', other: 'The book has {count} words.' },
  'words.goals': 'Goals for this book',
  'words.goalsNote': 'Kept on this device. Today counts what you type in the editor, less what you delete.',
  'words.bookGoal': 'Book goal, in words',
  'words.dailyGoal': 'Daily goal, in words',
  'words.goalHint': 'Leave it empty for no goal.',
  'words.goalRefused': 'Type a whole number of words from 1 to 10,000,000, or leave it empty.',
  'words.progress': '{value} of {goal} ({percent}%)',
  'words.reached': 'Daily goal reached: {goal} words today.',
  'notes.button': 'Notes',
  'notes.buttonCount': '· {count}',
  'notes.title': 'Notes',
  'notes.showInText': 'Show private notes in text',
  'notes.selection': 'Selected text',
  'notes.privateNote': 'Private note',
  'notes.thisChapter': 'This chapter',
  'notes.notebook': 'Notebook',
  'notes.noChapter': 'Open a chapter to see its notes.',
  'notes.synopsis': 'Synopsis',
  'notes.show': 'Show notes',
  'notes.showOpen': 'Open',
  'notes.showResolved': 'Resolved',
  'notes.showAll': 'All',
  'notes.inText': 'Notes in the order of the text',
  'notes.noneOpen': 'No open notes in this chapter.',
  'notes.noneResolved': 'No resolved notes in this chapter.',
  'notes.noneAll': 'No notes in this chapter yet. Select text to note a passage, or write below to note the whole chapter.',
  'notes.detached': 'Detached',
  'notes.detachedHelp': 'The text these notes were on was deleted or rewritten. Each keeps a quote of it.',
  'notes.resolvedCount': { one: '{count} resolved note is hidden. Choose Resolved or All to see it.', other: '{count} resolved notes are hidden. Choose Resolved or All to see them.' },
  'notes.onlyYou': 'Only you',
  'notes.word': 'Word',
  'notes.wordAuthor': 'Word comment',
  'notes.you': 'You',
  'notes.youInitial': 'Y',
  'notes.now': 'now',
  'notes.minutes': '{count} min',
  'notes.minutesLong': { one: '{count} minute ago', other: '{count} minutes ago' },
  'notes.hours': '{count} h',
  'notes.hoursLong': { one: '{count} hour ago', other: '{count} hours ago' },
  'notes.days': { one: '{count} day', other: '{count} days' },
  'notes.daysLong': { one: 'yesterday', other: '{count} days ago' },
  'notes.edited': 'Edited {when}',
  'notes.goTo': 'Go to “{quote}” in the text',
  'notes.untitled': 'Untitled note',
  'notes.wholeChapter': 'the whole chapter',
  'notes.ungrouped': 'Notes',
  'notes.replyShort': 'Reply',
  'notes.reply': 'Reply to the note on “{name}”',
  'notes.replyLabel': 'Your reply',
  'notes.replyPlaceholder': 'Write a reply',
  'notes.saveReply': 'Save reply',
  'notes.resolveShort': 'Resolve',
  'notes.resolve': 'Resolve the note on “{name}”',
  'notes.reopenShort': 'Reopen',
  'notes.reopen': 'Reopen the note on “{name}”',
  'notes.editShort': 'Edit',
  'notes.edit': 'Edit the note on “{name}”',
  'notes.done': 'Done',
  'notes.textLabel': 'The note on “{name}”',
  'notes.textLabelPlain': 'Note',
  'notes.titleLabel': 'Title',
  'notes.groupLabel': 'Group',
  'notes.deleteShort': 'Delete',
  'notes.delete': 'Delete the note on “{name}”',
  'notes.deleteAsk': 'Delete the note on “{name}” and its replies for good?',
  'notes.cancel': 'Cancel',
  'notes.confirmDelete': 'Delete for good',
  'notes.search': 'Search notebook',
  'notes.newNote': 'New note',
  'notes.noneNotebook': 'No notebook notes yet: characters, places, research, anything about the book.',
  'notes.noneFound': 'No notebook note has those words.',
  'notes.composerLabel': 'New note',
  'notes.composerChapter': 'Select text to anchor a note, or note the whole chapter',
  'notes.composerNotebook': 'Write a note for this book',
  'notes.onPassage': 'On “{quote}”',
  'notes.dropPassageShort': 'Remove',
  'notes.dropPassage': 'Note the whole chapter instead of “{quote}”',
  'notes.save': 'Save note',
  'notes.whoSees': 'Who can see this note',
  'notes.shared': 'Shared',
  'notes.onlyMe': 'Only me',
  'notes.sharedWith': 'Shared with',
  'notes.group.everyone': 'Everyone on the book',
  'notes.group.editor': 'Editors',
  'notes.group.beta-reader': 'Beta readers',
  'notes.group.proofreader': 'Proofreaders',
  'notes.group.person': 'You and {name}',
  'notes.invitee': 'Someone invited',
  'notes.sharedHelp': '{group} with notes access to this book will see it and can reply.',
  'notes.everyone': 'Everyone',
  'notes.composerHelp': 'Only you will ever see this. Notes are left out of EPUB, PDF and Word; the bundle keeps them.',
  'notes.unreadable': { one: "{count} note can't be read, so it isn't shown. Nothing in it is changed, and the bundle keeps it.", other: "{count} notes can't be read, so they aren't shown. Nothing in them is changed, and the bundle keeps them." },
  'notes.close': 'Close notes',

  'update.label': 'Update',
  'update.ready': 'A new version of Cockatrice is ready.',
  'update.apply': 'Update now',
  'update.updating': 'Saving your typing, then updating…',
  'update.failed': "Your latest typing couldn't be saved, so Cockatrice wasn't updated. Your text is still here.",
  'update.retry': 'Try again',

  'homeScreen.title': 'Add Cockatrice to your Home Screen',
  'homeScreen.body': "On iPhone and iPad, the browser may erase a site's storage after 7 days without a visit, and books written in the browser don't move to the Home Screen app. Add Cockatrice to your Home Screen first, and write there.",
  'homeScreen.steps': 'How to add it',
  'homeScreen.step1': 'Tap the Share button.',
  'homeScreen.step2': 'Choose Add to Home Screen.',
  'homeScreen.step3': 'Open Cockatrice from your Home Screen.',
  'homeScreen.keep': 'Keep writing in the browser',
  'homeScreen.banner': 'Books written in the browser stay in the browser, which may erase them after 7 days without a visit. Export a bundle often to keep a copy.',

  'status.saved': 'Saved on this device',
  'status.unsaved': 'Not saved yet',
  'status.saving': 'Saving…',
  'status.failed': "Couldn't save. Trying again…",
  // Fix-next 43: the phone bar's words, beside the book's title; the full ones above stay for screen readers.
  'status.savedShort': 'Saved',
  'status.unsavedShort': 'Not saved',
  'status.savingShort': 'Saving…',
  'status.failedShort': "Couldn't save",
  'status.local': 'Only on this device',

  'nav.label': 'Chapters',
  'editor.label': 'Chapter text',
  'editor.none': 'No chapter is open.',
  'skip.editor': 'Skip to the text',

  'book.label': 'Book',
  'book.new': 'New book',
  'book.open': 'Open book',
  'book.untitled': 'Untitled book',
  'book.unreadable': 'Book with unreadable details',
  'book.title': 'Book title',
  'book.language': 'Language',

  'chapters.new': 'New chapter',
  'chapters.empty': 'This book has no chapters yet.',
  'chapter.untitled': 'Untitled chapter',
  'chapter.more': 'More for {title}',
  'chapter.rename': 'Rename',
  'chapter.titleLabel': 'Chapter title',
  'chapter.save': 'Save title',
  'chapter.cancel': 'Cancel',
  'chapter.moveUp': 'Move up',
  'chapter.moveDown': 'Move down',
  'chapter.trash': 'Move to the trash',
  'chapter.unsaved': 'Not saved yet',

  'trash.heading': { one: '{count} chapter in the trash', other: '{count} chapters in the trash' },
  'trash.restore': 'Restore {title}',
  'trash.deleteForever': 'Delete {title} forever',
  'trash.empty': 'Empty the trash',
  'trash.deletedHere': 'Deleted {date} on this device',
  'trash.deletedElsewhere': 'Deleted {date} on another device',

  'confirm.deleteTitle': 'Delete “{title}” forever?',
  'confirm.deleteBody': "Its text and its history are deleted from this device. This can't be undone.",
  'confirm.emptyTitle': 'Empty the trash?',
  'confirm.emptyBody': {
    one: "{count} chapter and its history are deleted from this device. This can't be undone.",
    other: "{count} chapters and their history are deleted from this device. This can't be undone.",
  },
  'confirm.discardTitle': 'Discard this kept text?',
  'confirm.discardBody': "It is deleted from this device. This can't be undone.",
  'confirm.deleteAction': 'Delete chapter forever',
  'confirm.emptyAction': { one: 'Delete {count} chapter forever', other: 'Delete {count} chapters forever' },
  'confirm.discardAction': 'Discard kept text',
  'confirm.cancel': 'Cancel',

  'attention.heading': 'Needs your attention',
  'attention.copy': 'Text kept from a deleted chapter, {date}',
  'attention.copyOf': 'Typing kept from “{title}”, {date}',
  'attention.recover': 'Make it a chapter',
  'attention.discard': 'Discard',
  'attention.invalid': {
    one: "{count} record on this device can't be read. It is left as it is.",
    other: "{count} records on this device can't be read. They are left as they are.",
  },

  'long.body': 'This chapter has more than {words} words. Long chapters make the editor slower to open and to type in.',
  'long.split': 'Split it…',

  'split.title': 'Split “{title}”',
  'split.body': 'Pick where new chapters start. Each one starts at a heading or a scene break.',
  'split.heading': 'Heading “{title}”',
  'split.scene': 'Scene break {number}',
  'split.none': 'This chapter has no headings or scene breaks to split at.',
  'split.confirm': 'Split',
  'split.cancel': 'Cancel',

  'notice.saveFailed': "The chapter couldn't be saved, so it stays open. Your text is still here, and Cockatrice keeps trying.",
  'notice.autosaveFailed': "Your typing couldn't be saved on this device yet. Cockatrice keeps trying; keep this page open until it says saved.",
  'notice.writeFailed': "That change couldn't be saved on this device.",
  'notice.copyKept': 'Another tab changed this chapter, so your typing here was kept as a conflict copy, listed under “Needs your attention”.',
  'notice.unreadable': "This can't be read, so it isn't opened. Nothing in it is changed.",
  'notice.readFailed': "The book couldn't be read from this device just now. Try again.",
  'notice.titleRefused': "That title can't be used.",
  'notice.splitNotAPoint': "The chapter changed, so it wasn't split. Try again.",
  'notice.splitEmptyPiece': "A new chapter would have no text, so it wasn't split.",
  'notice.splitNotes': "A footnote would be cut off from its reference, so it wasn't split.",
  'notice.splitReadsBack': "The new chapters wouldn't read the same as this one, so it wasn't split.",
  'notice.splitTooLong': "A new chapter would be too long, so it wasn't split.",
  'notice.notesFailed': "Your notes couldn't be saved on this device yet. They are still here, and Cockatrice tries again as you type or close Notes.",
  'notice.notesJoined': 'Another tab changed a note you were typing in, so both versions are kept in it, one after the other. Open Notes to tidy it.',
  'notice.noteGone': 'That note was deleted in another tab.',
  'notice.noteFailed': "That note couldn't be saved on this device. What you wrote is still in Notes; try again.",
  'notice.notesLost': "Notes you typed for a chapter or note that another tab deleted for good weren't kept.",
  'notice.unsavedWaiting': "Typing from before the page last closed couldn't be saved into the book yet. It is kept on this device, and Cockatrice tries again each time it opens.",
  'notice.dismiss': 'Dismiss',

  'settings.title': 'Settings',
  'settings.sections': 'Settings sections',
  'settings.backHome': 'Library',
  'settings.backWrite': 'Back to writing',
  'settings.appearance': 'Appearance',
  'settings.writing': 'Writing',
  'settings.exports': 'Export defaults',
  'settings.backupsSync': 'Backups and sync',
  'settings.account': 'Account',
  'settings.help': 'Help',
  'settings.theme': 'Theme',
  'settings.themeHelp': 'Applies to the library and the writer. Book pages stay paper-coloured.',
  'settings.font': 'Manuscript font',
  'settings.sizeHelp': 'Only the writing column. Book pages follow the page size.',
  'settings.sizeValue': '{size} px',
  'settings.dailyGoal': 'Daily word goal',
  'settings.dailyGoalHelp': "For this book; the toolbar shows today's words toward it.",
  'settings.typewriter': 'Typewriter scrolling',
  'settings.typewriterHelp': 'Keep the current line centred on screen',
  'settings.smartQuotes': 'Smart quotes and dashes',
  'settings.smartQuotesHelp': 'Converts straight quotes and double hyphens as you type',
  'settings.spelling': 'Spelling language',
  'settings.spellingHelp': "This book's language: spelling and hyphenation follow it.",
  'settings.pageSize': 'Default page size',
  'settings.pageSizeHelp': 'The page size the book shows at beside the text',
  'settings.sceneBreak': 'Scene break symbol',
  'settings.sceneBreakHelp': 'Replaces * * * in EPUB and PDF',
  'settings.manuscript': 'Word in manuscript format',
  'settings.manuscriptHelp': '12 pt, double-spaced, for agents and publishers',
  'settings.backupFolder': 'Backup folder',
  'settings.syncOff': 'Sync is off',
  'settings.syncHelp': 'Encrypted on this device before it leaves. Needs a second sign-in step.',
  'settings.syncOn': 'Turn on sync…',
  'settings.accountHelp': 'Sign in to sync your books and share them.',
  'settings.accountNone': 'Not logged in',
  'settings.accountNoneHelp': 'Log in to see and write your books on this device, or start your free trial.',
  'settings.accountStart': 'Log in or start trial',
  'settings.accountChangeEmail': 'Change email',
  'settings.accountPaid': 'Bought. Includes updates for as long as the service runs.',
  'settings.accountTrial': { one: 'Trial: {count} day left.', other: 'Trial: {count} days left.' },
  'settings.accountEnded': 'Trial ended: the books are read only. You can still read and export them.',
  'settings.accountUnknown': 'Not confirmed on this device yet: the books are read only until it connects.',
  'settings.accountSignedOut': 'Logged out on this device',
  'settings.accountSignedOutHelp': "Your account ended this device's session. Log in again to keep your trial or purchase up to date here.",
  'settings.logOut': 'Log out',
  'settings.logOutHelp': "Your books stay on this device, hidden until you log in again. If you can't log in again (an account whose trial ended is deleted after 90 days), export them first.",
  'settings.logOutConfirm': 'Log out now',
  'settings.guide': 'Open the guide',
  'settings.guideHelp': 'What the app does and how, the tester guide.',
  'settings.size': 'Text size',
  'settings.paragraphLook': 'Paragraphs',
  'settings.autoConversion': 'Turn typed shortcuts into formatting',
  'settings.autoConversionHint': 'For example, “- ” at the start of a paragraph starts a list.',
  'settings.focusMode': 'Focus mode',
  'settings.keys': 'Keyboard shortcuts',
  'settings.book': 'This book',
  'settings.app': 'App',
  'offline.unavailable': "This browser can't keep Cockatrice for offline use. Your writing is still saved on this device.",
  'offline.preparing': 'Getting ready to work offline…',
  'offline.ready': 'Cockatrice works offline on this device.',
  'install.button': 'Install Cockatrice',
  'install.hint': 'Opens Cockatrice in a window of its own, from your apps.',

  'theme.system': 'System',
  'theme.light': 'Light',
  'theme.dark': 'Dark',
  'theme.sepia': 'Sepia',
  'font.serif': 'Newsreader (serif)',
  'font.sans': 'Source Sans 3 (sans serif)',
  'font.mono': 'Monospace',
  'look.book': 'Indented, as in a book',
  'look.block': 'Spaced apart, no indent',

  'command.focusMode': 'Focus mode on or off',
  'command.chapterList': 'Show or hide chapters',
  'command.previousChapter': 'Previous chapter',
  'command.nextChapter': 'Next chapter',
  'command.newChapter': 'New chapter',
  'command.settings': 'Settings',
  'command.help': 'Help',
  'command.find': 'Find and replace',
  'command.note': 'Private note on the selected text',

  'keys.change': 'Change the shortcut for {command}',
  'keys.changeShort': 'Change',
  'keys.remove': 'Remove the shortcut for {command}',
  'keys.removeShort': 'Remove',
  'keys.reset': 'Reset every shortcut',
  'keys.resetAsk': 'Reset every shortcut to its default? The keys you chose go.',
  'keys.resetConfirm': 'Reset',
  'keys.resetKeep': 'Keep mine',
  'keys.none': 'None',
  'keys.press': 'Press the new keys for “{command}”, or Escape to stop.',
  'keys.invalid': "Those keys can't be a shortcut.",
  'keys.typing': 'Add Ctrl, Alt or Cmd: on their own, those keys type text.',
  'keys.editor': 'The editor uses those keys.',
  'keys.taken': '“{command}” already uses those keys.',

  'key.ctrl': 'Ctrl',
  'key.alt': 'Alt',
  'key.shift': 'Shift',
  'key.meta': 'Super',
  'key.join': '{first}+{rest}',
  'key.PageUp': 'Page Up',
  'key.PageDown': 'Page Down',
  'key.Home': 'Home',
  'key.End': 'End',
  'key.ArrowUp': 'Up',
  'key.ArrowDown': 'Down',
  'key.ArrowLeft': 'Left',
  'key.ArrowRight': 'Right',
  'key.Enter': 'Enter',
  'key.Backspace': 'Backspace',
  'key.Delete': 'Delete',
  'key.Insert': 'Insert',
  'key.Space': 'Space',
  'key.function': 'F{number}',

  // The help dialog and `docs/guide.md` (part 11b, table M). A guide message is Markdown's inline text: `code`,
  // **strong** and *emphasis* only (M2, M3).
  // Part 38d: reading links (cockatrice-ui `CreateLink.jsx`, `Sharing.jsx`; what the design lacks in its tone).
  'share.title': 'Share a reading link',
  'share.readyTitle': 'Your reading link is ready',
  'share.close': 'Close',
  'share.name': 'Name this link',
  'share.nameHelp': 'Only you see this name',
  'share.unnamed': 'Reading link, {date}',
  'share.chapters': 'Chapters',
  'share.all': 'All chapters',
  'share.some': 'Choose chapters',
  'share.from': 'From',
  'share.to': 'To',
  'share.chapterOption': '{number}. {title}',
  'share.comments': 'Allow comments',
  'share.commentsHelp': 'Readers comment in the text; comments appear in your chapters.',
  'share.ends': 'End date',
  'share.endsHelp': 'Optional. The link turns off at the end of this day.',
  'share.keyNote': "The link carries the key, so our server can't read this copy. Anyone who has the link can.",
  'share.cancel': 'Cancel',
  'share.create': 'Create link',
  'share.creating': 'Creating…',
  'share.retry': 'Try again',
  'share.summaryFor': 'For',
  'share.summary': '· {chapters} · copy of the book as it is today, {date}',
  'share.summaryAll': 'all chapters',
  'share.summaryOne': 'chapter {from}',
  'share.summaryRange': 'chapters {from}–{to}',
  'share.link': 'Reading link',
  'share.copy': 'Copy link',
  'share.copied': 'Copied',
  'share.copyFailed': "Couldn't copy. Select the link and copy it.",
  'share.warnAnyone': "Anyone with this link can read this copy. Send it only to the people it's for.",
  'share.warnRevoke': "Turning the link off stops new reading. It can't take back what someone has already opened.",
  'share.warnCopy': 'Readers see the book as it is now. To give them later changes, share a new link.',
  'share.warnSize': 'This copy uses {size} of your 2 GB.',
  'share.done': 'Done',
  'share.purchase': 'Reading links come with the purchase. Buy Cockatrice to share a copy of your book with beta readers.',
  'share.error.range': 'The first chapter must come before the last.',
  'share.error.date': 'Choose a day from today on.',
  'share.error.read': "The book couldn't be read whole, so nothing was shared.",
  'share.error.tooLarge': 'This copy is too large to share. Choose fewer chapters.',
  'share.error.purchase': 'Reading links come with the purchase.',
  'share.error.tooMany': 'You have 100 open reading links. Revoke one in Sharing first.',
  'share.error.quota': 'Your open reading links use all of your 2 GB. Revoke one in Sharing first.',
  'share.error.today': "You've made 30 links today. Try again tomorrow.",
  'share.error.unfinished': "The copy didn't finish uploading. Check your connection and try again.",
  'share.error.failed': "Couldn't make the link. Try again.",
  // Part 38b: sharing with people (cockatrice-ui `ShareDialog.jsx`, `Sharing.jsx`; what the design lacks in its tone).
  'invite.title': 'Share “{title}”',
  'invite.linkTitle': 'Send the invite to {email}',
  'invite.email': 'Email address',
  'invite.emailPlaceholder': 'Add people by email',
  'invite.role': 'Role',
  'invite.roleName.editor': 'Editor',
  'invite.roleName.beta-reader': 'Beta reader',
  'invite.roleName.proofreader': 'Proofreader',
  'invite.roleHelp.editor': 'Editor reads the manuscript and writes notes. You can change any of this per person below.',
  'invite.roleHelp.beta-reader': 'Beta reader reads the manuscript and the shared notes. You can change any of this per person below.',
  'invite.roleHelp.proofreader': 'Proofreader reads the manuscript and writes notes. You can change any of this per person below.',
  'invite.roleGroup': 'Their notes are seen by you and the others in this role.',
  'invite.invite': 'Invite',
  'invite.inviting': 'Inviting…',
  'invite.people': 'People with access',
  'invite.you': 'You',
  'invite.youHelp': 'Owns the book and all notes',
  'invite.owner': 'Owner',
  'invite.manuscript': 'Manuscript',
  'invite.read': 'Read',
  'invite.readHelp': 'Can read the chapters you share. Cannot change the text.',
  'invite.notes': 'Notes',
  'invite.notes.none': 'None',
  'invite.notes.read': 'Read',
  'invite.notes.write': 'Write',
  'invite.notesHelp.none': 'Notes are hidden.',
  'invite.notesHelp.read': 'Sees shared notes, cannot add or reply.',
  'invite.notesHelp.write': 'Adds notes and replies. Only you can delete or resolve their notes.',
  'invite.export': 'Can export (EPUB, PDF, Word)',
  'invite.exportHelp': 'This only hides the buttons: anyone who can read the book can copy it.',
  'invite.ends': 'Access ends',
  'invite.endsDate': 'Access end date',
  'invite.chaptersAll': 'All chapters…',
  'invite.chaptersOne': 'Only chapter {number}…',
  'invite.chaptersSome': 'Only chapters {from}–{to}…',
  'invite.chaptersNone': 'No chapter…',
  'invite.save': 'Save changes',
  'invite.saving': 'Saving…',
  'invite.saved': 'Changes for {email} are saved.',
  'invite.remove': 'Remove access',
  'invite.removeAsk': "Remove {email}? They can't open the book any more. What they have already read stays with them.",
  'invite.removeConfirm': 'Remove access',
  'invite.removed': '{email} no longer has access.',
  'invite.changeLabel': 'Change {email}, {role}',
  'invite.resend': 'Resend',
  'invite.resendLabel': 'Resend the invite to {email}',
  'invite.renew': 'Send a new invite',
  'invite.renewLabel': 'Send {email} a new invite',
  'invite.standing.unsent': 'Invited as {role} · not sent to the server yet',
  'invite.standing.pending': 'Invited as {role} · not accepted yet',
  'invite.standing.accepted': '{role} · accepted {date}',
  'invite.standing.acceptedSome': '{role} · accepted',
  'invite.standing.lost': 'Reset their password: needs a new invite',
  'invite.standing.ended': 'Access ended',
  'invite.purchase': 'Inviting people comes with the purchase. The people you already invited keep their access.',
  'invite.makeLink': 'Create a reading link instead…',
  'invite.lockNote': 'Your private notes are never shared, whatever access you give.',
  'invite.sendYourself': 'Send this link to {email} yourself. Cockatrice never sends it for you.',
  'invite.link': 'Invite link',
  'invite.mail': 'Email it',
  'invite.sheet': 'Share…',
  'invite.shared': 'The invite link is shared.',
  'invite.sheetText': 'An invitation to read “{title}” in Cockatrice.',
  'invite.mailSubject': 'An invitation to read “{title}”',
  'invite.mailBody': "I'd like you to read “{title}” in Cockatrice. Open this link to accept: {url}",
  'invite.warnUnsent': "Couldn't reach the server, so the invite is kept here and sent as soon as the server answers. The link works from then.",
  'invite.warnSelf': 'The link holds their key, so our server never sees it and can never read the book.',
  'invite.warnAddress': 'Only an account at {email} can accept it. If anyone else gets the link, they still cannot open the book.',
  'invite.warnCopy': 'They see your changes as you write. Removing them stops that, but what they already read stays with them.',
  'invite.problem.too-large': "A chapter is over 1 MB, too large to send: the people you share with don't get it. Split it to send it.",
  'invite.problem.over-quota': "Your 2 GB is full, so the people you share with don't get your latest changes. Revoke a reading link or remove a chapter from sharing.",
  'invite.problem.share-full': "This book has more chapters than can be shared (1 000), so the people you share with don't get the rest.",
  'invite.problem.failed': "Couldn't send your latest changes to the people you share with. It tries again as you write, or when you're back online.",
  'invite.problem.settings': "The server didn't take a person's latest settings, so they keep what they had. Open Share and change them again.",
  'invite.problem.stale': "A chapter's latest changes weren't sent: the server holds a newer copy, sent from another device or with a clock ahead of this one. Check this device's date and time. It tries again as you write.",
  'invite.problem.invite-unpaid': "An invite wasn't sent: inviting people comes with the purchase. Buy Cockatrice to send it, or remove the person in Share.",
  'invite.problem.invite-full': "An invite wasn't sent: this book is shared with 50 people already. Remove someone in Share to send it.",
  'invite.problem.invite-later': "An invite wasn't sent yet: the server takes 100 invites a book a day. It tries again as you write.",
  'invite.problem.note-too-large': "A shared note is too long to send (over 10 000 characters), so the people you share it with don't get it. Shorten it to send it.",
  'invite.problem.notes-full': "Your notes on this book have reached the limit the server keeps (5 000 notes, 4 MB), so new shared notes aren't sent. Delete some shared notes to send more.",
  'invite.problem.elsewhere': "This book is also shared from another device, with people this device doesn't know. So no one is cut off, your changes aren't sent from here. Export a bundle on that device and use Update this book here.",
  'invite.error.email': 'Enter an email address.',
  'invite.error.far': 'Choose a day within ten years.',
  'invite.error.settings': "The server didn't take these settings. Check them and try again.",
  'invite.error.already': '{email} already has access.',
  'invite.error.purchase': 'Inviting people comes with the purchase.',
  'invite.error.full': 'This book is shared with 50 people already. Remove someone first.',
  'invite.error.today': "You've sent 100 invites for this book today. Try again tomorrow.",
  'invite.error.ended': 'Sharing this book was ended on another device. Close this and try again.',
  'invite.error.failed': "Couldn't reach the server. Try again.",
  'sharing.title': 'Sharing',
  'sharing.show': 'Show',
  'sharing.filter.shared': 'Shared books',
  'sharing.filter.soon': 'Ending soon',
  'sharing.filter.all': 'All books',
  'sharing.tilePerson': 'person with access',
  'sharing.tilePeople': 'people with access',
  'sharing.tileSoon': 'access ends this week',
  'sharing.tileSoons': 'accesses end this week',
  'sharing.manuscript': 'Manuscript',
  'sharing.notes': 'Notes',
  'sharing.comment': 'Comment',
  'sharing.inDays': { one: 'in {count} day', other: 'in {count} days' },
  'sharing.people': { one: '{count} person', other: '{count} people' },
  'sharing.notShared': 'Not shared',
  'sharing.onlyYou': 'Not shared. Only you can open this book.',
  'sharing.manage': 'Manage',
  'sharing.manageLabel': 'Manage who can open {title}',
  'sharing.change': 'Change',
  'sharing.changeLabel': 'Change {email}',
  'sharing.remove': 'Remove',
  'sharing.removeLabel': 'Remove {email}',
  'sharing.removeFailed': "Couldn't reach the server. {email} still has access; try again.",
  'sharing.person.unsent': 'Not sent yet',
  'sharing.person.pending': 'Not accepted yet',
  'sharing.person.lost': 'Needs a new invite',
  'sharing.person.accepted': 'Accepted',
  'sharing.person.acceptedOn': 'Accepted {date}',
  'sharing.person.notes': { one: '{count} note', other: '{count} notes' },
  'sharing.personEnded': 'access ended',
  'sharing.noneSoon': 'No access ends this week.',
  'sharing.elsewherePeople': { one: '{count} person invited from another device', other: '{count} people invited from other devices' },
  'sharing.elsewherePeopleHelp': "This device doesn't hold their invites' keys, so it can't change or remove them one by one. End all shared books stops them too.",
  'sharing.endAllShares': 'End all shared books',
  'sharing.endAllSharesHelp': 'Lost a device? This stops everyone you invited, on every book, at once, those invited from other devices too.',
  'sharing.endAllSharesAsk': "End every shared book of your account? The people you invited can't open them any more. What they already read stays with them.",
  'sharing.endAllSharesConfirm': 'End all shared books',
  'sharing.endedAllShares': { one: '{count} shared book ended.', other: '{count} shared books ended.' },
  'sharing.lead': 'Everyone who can open one of your books, in one place.',
  'sharing.summaryLabel': 'Summary',
  'sharing.tileBook': 'book shared',
  'sharing.tileBooks': 'books shared',
  'sharing.tileLink': 'open reading link',
  'sharing.tileLinks': 'open reading links',
  'sharing.tileComment': 'comment from readers',
  'sharing.tileComments': 'comments from readers',
  'sharing.share': 'Share…',
  'sharing.shareLabel': 'Share {title}',
  'sharing.bookSummary': { one: '{count} reading link', other: '{count} reading links' },
  'sharing.who': 'Who',
  'sharing.chapters': 'Chapters',
  'sharing.until': 'Until',
  'sharing.activity': 'Activity',
  'sharing.actions': 'Actions',
  'sharing.kind': 'Reading link · no account',
  'sharing.all': 'All',
  'sharing.chosen': 'Chosen chapters',
  'sharing.copyFrom': '{chapters} · copy from {date}',
  'sharing.chapterCount': { one: '{count} chapter', other: '{count} chapters' },
  'sharing.noEnd': 'No end date',
  'sharing.comments': { one: '{count} comment', other: '{count} comments' },
  'sharing.noComments': 'No comments',
  'sharing.copyLink': 'Copy link',
  'sharing.copyLinkLabel': 'Copy the link {name}',
  'sharing.copied': 'The link {name} is copied.',
  'sharing.linkOf': 'The link {name}',
  'sharing.revoke': 'Revoke',
  'sharing.revokeLabel': 'Revoke {name}',
  'sharing.revokeAsk': 'Revoke {name}? New reading stops at once. Comments already sent come to your notes first.',
  'sharing.revokeConfirm': 'Revoke link',
  'sharing.keep': 'Keep it',
  'sharing.revoked': '{name} is revoked.',
  'sharing.revokeFailed': "Couldn't reach the server. {name} is still open; try again.",
  'sharing.ended': 'Ended and revoked access · {count}',
  'sharing.endedHelp': 'Revoking a reading link stops new reading. It cannot take back a copy someone has already opened.',
  'sharing.end.revoked': 'Revoked {date}',
  'sharing.end.expired': 'Ended {date}',
  'sharing.end.disabled': 'Turned off by us {date}',
  'sharing.end.abandoned': "Didn't finish uploading",
  'sharing.end.gone': 'Deleted from the server',
  'sharing.disabledWhy': 'Why: {reason}',
  'sharing.reason.illegal': 'reported as illegal',
  'sharing.reason.copyright': 'reported as breaking copyright',
  'sharing.reason.privacy': "reported as breaking someone's privacy",
  'sharing.reason.spam': 'reported as spam',
  'sharing.reason.other': 'reported',
  'sharing.paused.downloads': 'Paused: read 50 times this month. It opens again {date}.',
  'sharing.paused.egress': "Paused: your links reached this month's 10 GB. They open again {date}.",
  'sharing.uploading': "Didn't finish uploading",
  'sharing.elsewhere': { one: '{count} reading link made on another device', other: '{count} reading links made on other devices' },
  'sharing.elsewhereHelp': "This device doesn't hold their keys, so it can't show or revoke them one by one. End all reading links stops them too.",
  'sharing.endAll': 'End all reading links',
  'sharing.endAllHelp': 'Lost a device? This stops every reading link of your account at once, those made on other devices too.',
  'sharing.endAllAsk': "End every reading link of your account? Readers can't open them any more. Comments still waiting stay on the server for 90 days for a device that holds their link.",
  'sharing.endAllConfirm': 'End all links',
  'sharing.endedAll': { one: '{count} link ended.', other: '{count} links ended.' },
  'sharing.endAllFailed': "Couldn't reach the server. Nothing was ended; try again.",
  'sharing.loading': 'Getting your links…',
  'sharing.failed': "Couldn't reach the server. The links below are as this device last knew them.",
  'sharing.retry': 'Try again',
  'sharing.noneTitle': 'Nothing is shared yet.',
  'sharing.none': 'Open a book and press Share to invite people or make a reading link.',
  'notes.reader': 'Reading link',
  'notes.readerAuthor': 'A reader',
  'notes.readerVia': 'Reading link {name}',
  'notes.replyToReader': 'The reader sees your reply.',
  'notes.replyEnded': 'The link has ended: only you see this reply.',
  'notes.sendReply': 'Send reply',
  'notes.replyFailed': "Couldn't send the reply. It's kept here; try again.",
  'notes.replyNotSent': "The link has ended, so the reply wasn't sent. Save reply keeps it here, for you only.",
  'notes.replyTooLong': 'This reply is too long to send to the reader. Shorten it; it is kept here meanwhile.',
  'notes.sent': 'Sent',
  'guide.title': 'Cockatrice tester guide',
  'guide.intro': 'A writing app for novels. You write chapters; a live preview shows them as book pages; you export a PDF, an EPUB or a Word file. Everything stays on your device.',
  'guide.start.heading': 'Before you start',
  'guide.start.address': '**This is a test build on a temporary address.** Your books live in this browser, at this address only.',
  'guide.start.sync': "**There is no sync yet.** Another browser or device doesn't see your books.",
  'guide.start.bundle': "**Keep backups.** In Chrome or Edge on a computer: Settings → Backups and sync → **Choose a folder…**; each changed book is saved there once a day as a bundle, the last 14 kept. Elsewhere the app reminds you after 7 days of changes without one: **Save the backup** (or **Share the backup**). **Export** → **Bundle** saves one any time, and **Import…** → **Bundle…** on My books brings it back. Settings and My books say when each book was last backed up.",
  'guide.start.plain': "**Backups are plain files, not encrypted:** anyone who can open them can read your books. On an iPhone or iPad a saved or shared file may go to iCloud. To lock them, Settings → Backups and sync → **Set password**: backups are then `.cockatrice-backup` files nobody can open without your backup password, not even us, and a forgotten one can't be recovered. Now and then the app asks you to type it, to check you still know it. **Export** → **Bundle** stays a plain file.",
  'guide.start.ios': '**iPhone and iPad:** add the app to your Home Screen (it asks). Books written in Safari itself may be erased after 7 days without a visit.',
  'guide.books.heading': 'Books and chapters',
  'guide.books.book': 'The app opens on **My books**: the book you wrote in last, with **Continue writing**, and every book as a cover; press one to open it, and **Sort** to order them. **New book** starts one. In the writer, **Library** goes back to them.',
  'guide.books.chapter': "In the writer the rail beside the text numbers the chapters; its top button (on a phone, the bar's) lists them by title. **New chapter** adds one. Each chapter's **⋯** menu has **Rename**, **Move up**, **Move down** and **Move to the trash**; Alt+↑ and Alt+↓ on a chapter, or dragging it, reorder too. The menus (Export, More, Import…, New book on a phone) open with a click, Enter or ↓; the arrow keys move through them and Escape closes them.",
  'guide.books.trash': '**Trash:** restore a chapter, delete it forever, or empty the trash.',
  'guide.books.split': 'A chapter over the long-chapter limit offers **Split it…** at its headings or scene breaks.',
  'guide.books.bundle': "**Import…** → **Bundle…** on My books (on a narrow screen, **New book** → **Bundle…**) brings back a book from a bundle made with **Export** → **Bundle**: its chapters, trash, history, pictures, notes and comments, as they were. A locked backup asks for its backup password first. A book already on this device comes in as a copy beside it, or **Update this book…** brings the bundle's changes into it (merged where both changed, your text kept as a conflict copy, nothing deleted), shown to you before **Apply the update**. You can keep writing meanwhile: the book shows on My books only once it is all in (**Open the book** opens it), and **Cancel** leaves nothing behind.",
  'guide.books.details': '**Book title** and **language:** **Book details** on My books, or Settings → This book and Writing.',
  'guide.books.import': '**Import…** → **Word or Markdown file…** on My books (on a narrow screen, **New book** → **Word or Markdown file…**) makes a new book from a Word file (.docx) or a Markdown or text file. It splits at the headings (in a text file, where each line is a paragraph, at lines like “Chapter 1”, “Prologue” or numerals “I”, “II”, “III”), shows the chapters and what to check, and stores nothing until you press **Import book**. In a Word file, a quote style (Word’s Quote, LibreOffice’s Block Quotation) comes in as a quote, and an empty line with a rule under it (LibreOffice’s Horizontal Line, or the line Word draws when you type three hyphens) as a scene break. A Word file exported from Cockatrice goes back into its book with More → **Re-import Word file…**.',
  'guide.books.reimport': "More → **Re-import Word file…** brings an edited DOCX export back into the open book, with any tracked changes accepted. Each chapter is matched to its own, and the review shows what each would change: take or skip each one; nothing changes until **Apply**. A copy of each chapter taken is kept in its history first, and one you also changed here since the export keeps your text as a conflict copy. A chapter the file titles otherwise is offered **Rename it**, ticked when only the file changed the title since the export, unticked when you renamed it here too. A chapter missing from the file stays as it is. The file's comments are kept with their chapters, each with its author's name, and go into the Markdown bundle; the app doesn't show them yet. The title page, front and back matter and chapter numbers aren't read back.",
  'guide.format.heading': 'Formatting',
  'guide.format.pages': 'Inside a book the bar switches between **Writing** and **Formatting**. Formatting keeps the pages in view while you change them.',
  'guide.format.templates': '**Templates** are looks you can use in any book. Press a card to use it, its star to move it to the front, and its three dots to **Duplicate** or **Delete** it. **New template** starts from the look the book has now.',
  'guide.format.linked': 'Books using the same template follow it: change a setting in one and the others change too. Changing a built-in template (Classic, Elegant, Modern, Large print) makes your own copy.',
  'guide.format.heading2': '**Chapter heading:** show or hide it; set the number, title and subtitle apart: font, alignment, bold, italic, ALL CAPS, size and fade. The number can read 1, One, Chapter One or Chapter 1. Up and down arrows set the space between the lines.',
  'guide.format.image': '**Chapter image:** **Upload** a picture, or pick one from the **Gallery**: your own images and ornaments from us. Tick **Use individual images** to give each chapter its own. Once one shows you can place it above, between, below or behind the heading, align, fade and size it. **Bleed image** runs it off the page edge in the PDF, for printed books only.',
  'guide.format.subtitle': 'Each chapter has its own subtitle or character name, written under **Chapter subtitle or character name** while that chapter is open.',
  'guide.writing.heading': 'Writing',
  'guide.writing.save': 'Type; it saves by itself. The bar says **Saving…**, then **Saved on this device** (on a phone, **Saved**). Reloading or closing the tab straight after typing keeps it too.',
  'guide.writing.enter': '**Enter** starts a new paragraph. Enter on an empty line leaves an empty line.',
  'guide.writing.click': 'Click anywhere in the text pane to put the caret there.',
  'guide.writing.marks': 'Formatting comes from what you type; the marks hide once you leave them:',
  'guide.marks.type': 'Type',
  'guide.marks.result': 'You get',
  'guide.marks.italic': '`*word*` or `_word_`',
  'guide.marks.italicResult': '*italic*',
  'guide.marks.bold': '`**word**`',
  'guide.marks.boldResult': '**bold**',
  'guide.marks.smallCaps': '`^^Word^^`',
  'guide.marks.smallCapsResult': 'small caps',
  'guide.marks.heading': "`# Title` (at a line's start)",
  'guide.marks.headingResult': 'heading (`##`, `###` for smaller)',
  'guide.marks.list': "`- ` or `1. ` (at a line's start)",
  'guide.marks.listResult': 'list',
  'guide.marks.quote': "`> ` (at a line's start)",
  'guide.marks.quoteResult': 'quote',
  'guide.marks.link': '`[words](https://…)`',
  'guide.marks.linkResult': 'link',
  'guide.writing.images': "**Images:** the picture button over the text (**Insert image**), or paste or drop a picture (JPEG, PNG or WebP) in the text. It goes in at the cursor, named after its file; that name is its description, which you can edit. Pictures are kept at up to 1600 pixels, up to 100 MB a book.",
  'guide.writing.words': "**Word count:** the bar shows the chapter's words (on a phone, More → **Word count**); press it for the selection's, the book's and today's, and to set a book goal and a daily goal. Notes count; note numbers and picture descriptions don't. Goals and today's words are kept on this device; today counts what you type, less what you delete.",
  'guide.writing.notes': "**Notes:** **Notes** in the bar (on a phone, in More) opens them beside the text (on a phone, over it). **This chapter** lists its notes in the order of the text: select text and press **Private note** (Ctrl+Shift+M, Cmd+Shift+M on a Mac) to note it, or write in the box to note the whole chapter. Each note has **Reply**, **Resolve**, **Edit** and **Delete** (which asks first); its quote takes you to its passage, and one whose text was deleted is listed under **Detached**. The chapter's synopsis is there too. **Notebook** holds the book's notes, each with a title and a group (characters, places, anything); search them. **Show private notes in text** marks yours in the text. Notes save as you type, are only yours, and are never printed or exported with the book; only the bundle keeps them. Word comments a re-import kept are notes too.",
  'guide.writing.find': "**Find:** **Find** in the bar (on a phone, in More), or Ctrl+F (Cmd+F on a Mac). It searches the text as you see it, in this chapter or the whole book; **Regular expression** takes patterns. **Replace** changes the match shown, **Replace all** every one, and **Undo replace all** puts them back. A match across a line break, a picture or a note is left as it is. On a phone the panel takes the bar's place, with the replace controls under **Options and replace**; **Close find** brings the bar back.",
  'guide.writing.undo': '**Undo:** Ctrl+Z (Cmd+Z on a Mac). **Redo:** Ctrl+Shift+Z.',
  'guide.writing.plain': "Don't want marks to turn into formatting? Settings → Writing → turn off “Turn typed shortcuts into formatting”.",
  'guide.preview.heading': 'Preview and export',
  'guide.preview.pane': 'The pages beside the text show the chapter as a book. **Layout:** **Side by side**, **Pages** (the pages alone) or **Text** (the text alone). **Page size:** the menu above the pages lists devices by name, under their brands, and the A5 PDF. Star a device to keep it under **Favourites** at the top of the menu.',
  'guide.preview.book': 'Leaf through the pages with **Back** and **Next** under them, a click on a page or the mouse wheel, and jump with **Previous chapter** and **Next chapter**; your cursor stays put, and typing brings the pages back to it.',
  'guide.preview.print': '**A5 PDF** shows the book as it prints, each chapter starting on a right-hand page. Choose **One page** or **Two pages** where two fit, and press **Enlarge…** on a page for a closer look.',
  'guide.preview.phone': 'On a phone, switch between **Text** and **Pages** at the foot of the screen, or swipe sideways.',
  'guide.preview.pdf': "**Export** → **PDF** (in the writer or on My books) makes the whole book; **Save the PDF** when it's ready.",
  'guide.preview.epub': "**Export** → **EPUB** makes the whole book as an ebook, with its typefaces and pictures; **Save the EPUB** when it's ready. An e-reader sets the pages itself, so they won't match the preview's exactly.",
  'guide.preview.docx': "**Export** → **Word…** makes the whole book as a Word file: in the **book theme**, as the PDF looks, or as a **manuscript** for agents and editors (12 pt Times New Roman or Courier New, double-spaced, your surname, the title and the page number at the top). **Save the DOCX** when it's ready. A copy of each chapter as exported is kept on this device for a year.",
  'guide.preview.bundle': "**Export** → **Bundle** saves the whole book as a zip: its chapters as Markdown files, its images as they are stored, and its notes, the Word comments a re-import kept among them.",
  'guide.settings.heading': 'Settings',
  'guide.settings.editor': "Settings is a page of its own (Escape, or **Library**, goes back). **Appearance:** theme (dark, light, sepia or the system's), manuscript font, text size, paragraphs (indented as in a book, or spaced apart). **Writing:** the book's daily goal, typed shortcuts, focus mode, spelling language. **Export defaults:** the page size the preview starts with.",
  'guide.settings.keys': '**Keyboard shortcuts:** change or remove any. Defaults: Help F1, Find Ctrl+F, Focus mode Ctrl+Shift+F, chapters Ctrl+Shift+E, previous and next chapter Alt+Page Up / Page Down, new chapter Ctrl+Shift+Enter, settings Ctrl+, (Cmd on a Mac).',
  'guide.settings.focus': 'More → **Focus mode** hides everything but the text. Escape leaves it.',
  'guide.good.heading': 'Good to know',
  'guide.good.help': "**Help:** this guide, from More → **Help** or F1. It opens by itself on your first visit, and again when this guide changes.",
  'guide.good.offline': '**Works offline** once the settings say so. Install it from the browser (Settings → App) to open it like an app.',
  'guide.good.tabs': '**One tab edits at a time.** A second tab offers **Use here** to move the editing to it.',
  'guide.good.updates': '**Updates:** when a new version is ready, **Update now** saves your typing first.',
  'guide.good.attention': "If the app keeps typing it couldn't save to a chapter, it shows it under **Needs your attention**: make it a chapter or discard it.",
  'guide.good.privacy': "**Privacy:** your books stay on your device; nothing you write is sent anywhere. Cloudflare, which serves the app, logs your IP address when the app loads, as web hosts do. Cockatrice has no analytics and keeps nothing else about you.",
  'guide.later.heading': 'Not in this build yet',
  'guide.later.body': "Sync between devices, accounts, beta-reader links. Scene breaks, centered text and footnotes can't be typed yet. The privacy policy and the terms aren't published yet: they come before accounts do.",

  // The component library's own words (PLAN.md §0; `src/ui/text.ts`): cockatrice-ui's built-in English, by component.
  'ui.close': 'Close',
  'ui.confirm-dialog.cancel': 'Cancel',
  'ui.confirm-dialog.confirm': 'Confirm',
  'ui.confirm-dialog.busy': 'Working…',
  'ui.confirm-dialog.type': 'Type {word} to confirm',
  'ui.toast.region': 'Notifications',
  'ui.toast.dismiss': 'Dismiss',
  'ui.inline-confirm.delete': 'Delete',
  'ui.inline-confirm.cancel': 'Cancel',
  'ui.inline-confirm.message': "Delete for good? This can't be undone.",
  'ui.app-header.wordmark': 'Cockatrice',
  'ui.app-header.label': 'Main',
  'ui.breadcrumb.label': 'Breadcrumb',
  'ui.section-nav.label': 'Sections',
  'ui.bottom-bar.label': 'Views',
  'ui.pager.label': 'Pages',
  'ui.pager.previous': 'Previous',
  'ui.pager.next': 'Next',
  'ui.pagination.label': 'Pagination',
  'ui.pagination.previous': 'Previous',
  'ui.pagination.next': 'Next',
  'ui.pagination.page': 'Page {page}',
  'ui.pagination.compact': 'Page {page} of {count}',
  'ui.stepper.count': 'Step {step} of {total}',
  'ui.banner.dismiss': 'Dismiss',
  'ui.spinner.label': 'Loading',
  'ui.progress-bar.label': 'Progress',
  'ui.meter.label': 'Storage',
  'ui.meter.unit': 'GB',
  'ui.meter.free': 'Free',
  'ui.meter.used': '{used} of {max} used',
  'ui.meter.over': '{used} of {max} · {over} over',
  'ui.note-highlight.shared': 'Shared note: open',
  'ui.note-highlight.private': 'Private note: open',
  'ui.note-highlight.editor': "Editor's note: open",
  'ui.copy-field.label': 'Link',
  'ui.copy-field.copy': 'Copy',
  'ui.copy-field.copied': 'Copied',
  'ui.copy-field.status': 'Copied to the clipboard',
  'ui.note-card.private': 'Private note',
  'ui.note-card.by': 'Note by {author}',
  'ui.note-card.by-you': 'Note by you',
  'ui.note-card.only-you': 'Only you',
  'ui.note-card.detached': 'The text this note was on has been deleted.',
  'ui.note-card.go-to': 'Go to “{quote}” in the text',
  'ui.note-card.replies': 'Replies',
  'ui.note-card.resolved': 'Resolved',
  'ui.note-card.reply': 'Reply',
  'ui.note-card.resolve': 'Resolve',
  'ui.note-card.reopen': 'Reopen',
  'ui.note-card.edit': 'Edit',
  'ui.note-card.delete': 'Delete',
  'ui.note-composer.help-shared': 'Everyone with access to notes on this book can see it.',
  'ui.note-composer.help-private': "Only you will ever see this. It can't be shared later — write a shared note instead.",
  'ui.note-composer.shared': 'Shared',
  'ui.note-composer.private': 'Only me',
  'ui.note-composer.label': 'New note',
  'ui.note-composer.placeholder': 'Write a note…',
  'ui.note-composer.who': 'Who can see this note',
  'ui.note-composer.cancel': 'Cancel',
  'ui.note-composer.save': 'Save note',
  'ui.access-control.editor': 'Editor',
  'ui.access-control.proofreader': 'Proofreader',
  'ui.access-control.beta-reader': 'Beta reader',
  'ui.access-control.co-author': 'Co-author',
  'ui.access-control.custom': 'Custom',
  'ui.access-control.manuscript': 'Manuscript',
  'ui.access-control.manuscript-read': 'Read',
  'ui.access-control.manuscript-read-help': "Can read every shared chapter. Can't change the text.",
  'ui.access-control.manuscript-suggest': 'Suggest',
  'ui.access-control.manuscript-suggest-help': 'Changes arrive as suggestions you accept or reject.',
  'ui.access-control.manuscript-edit': 'Edit',
  'ui.access-control.manuscript-edit-help': 'Changes the text directly. Every change is kept in History.',
  'ui.access-control.notes': 'Notes',
  'ui.access-control.notes-none': 'None',
  'ui.access-control.notes-none-help': "Doesn't see shared notes.",
  'ui.access-control.notes-read': 'Read',
  'ui.access-control.notes-read-help': "Sees your shared notes. Can't add any.",
  'ui.access-control.notes-write': 'Write',
  'ui.access-control.notes-write-help': 'Sees shared notes and adds their own. Your private notes are never shown.',
  'ui.access-control.invited': 'Invited · not accepted yet',
  'ui.access-control.presets': 'Start from a role',
  'ui.access-control.also': 'Also',
  'ui.access-control.export': 'Can export a copy',
  'ui.access-control.ends': 'Access ends on',
  'ui.access-control.private': 'Your private notes are never shared.',
  'ui.access-control.remove': 'Remove access',
  'ui.share-link-card.label': 'Reading link: {name}',
  'ui.share-link-card.revoked': 'Revoked',
  'ui.share-link-card.ended': 'Ended',
  'ui.share-link-card.chapters': 'Chapters',
  'ui.share-link-card.copy-of': 'Copy of',
  'ui.share-link-card.comments': 'Comments',
  'ui.share-link-card.comments-on': 'On — readers can leave notes',
  'ui.share-link-card.comments-off': 'Off',
  'ui.share-link-card.ends': 'Ends',
  'ui.share-link-card.never': 'Never — until you revoke it',
  'ui.share-link-card.opened': 'Opened',
  'ui.share-link-card.not-yet': 'Not yet',
  'ui.share-link-card.times': { one: '{count} time', other: '{count} times' },
  'ui.share-link-card.gone': 'This link no longer opens anything.',
  'ui.share-link-card.link': 'Reading link',
  'ui.share-link-card.warning-anyone': 'Anyone with this link can read the chapters it includes.',
  'ui.share-link-card.warning-copy': "It shows the copy you made on the date above. Later changes aren't in it.",
  'ui.share-link-card.warning-notes': 'Your notes are never in a reading link.',
  'ui.share-link-card.change': 'Change…',
  'ui.share-link-card.revoke': 'Revoke link',
  'ui.recovery-key.label': 'Recovery key',
  'ui.recovery-key.groups': { one: '{label}, {count} group', other: '{label}, {count} groups' },
  'ui.recovery-key.download': 'Download',
  'ui.recovery-key.print': 'Print',
  'ui.recovery-key.copy': 'Copy',
  'ui.backup-codes.label': 'Backup codes',
  'ui.backup-codes.download': 'Download',
  'ui.backup-codes.copy': 'Copy',
  'ui.backup-codes.print': 'Print',
  'ui.backup-codes.confirm': 'I have saved these codes somewhere safe',
  'ui.avatar-group.more': '{names} and {count} more',
  'ui.avatar-group.and': '{names} and {last}',
  'ui.table.actions': 'Actions',
  'ui.table.empty': 'Nothing here yet.',
  'ui.list.selected': 'selected',
  'ui.saved-indicator.saved': 'Saved',
  'ui.saved-indicator.saving': 'Saving…',
  'ui.saved-indicator.offline': 'Offline — not saved yet',
  'ui.saved-indicator.error': 'Not saved — try again',
  'ui.word-goal.today': 'Today',
  'ui.word-goal.label': "Today's word goal",
  'ui.word-goal.words': '{words} / {goal} words',
  'ui.word-goal.met': 'Goal met',
  'ui.cover.title-by': '{title}, by {author}',
  'ui.cover.by': 'by {author}',
  'ui.cover.label': 'Book cover',
  'ui.cover.none': 'No cover yet',
  'ui.cover.add': 'Add one',
  'ui.book-card.words': '{words} words',
  'ui.new-book-tile.label': 'New book',
  'ui.new-book-tile.hint': 'or import DOCX / bundle',
  'ui.manuscript.label': 'Manuscript',
  'ui.manuscript.text-of': 'Text of {title}',
  'ui.chapter-list.label': 'Chapters',
  'ui.chapter-list.drag': 'Drag to reorder',
  'ui.chapter-list.words': '{words} words',
  'ui.chapter-item.label': 'Chapter {number}: {title}',
  'ui.chapter-item.more': 'More for chapter {number}, {title}',
  'ui.format-toolbar.label': 'Formatting',
  'ui.format-toolbar.bold': 'Bold',
  'ui.format-toolbar.italic': 'Italic',
  'ui.format-toolbar.small-caps': 'Small caps',
  'ui.format-toolbar.quote': 'Quote',
  'ui.format-toolbar.picture': 'Insert picture',
  'ui.format-toolbar.title': '{label} ({shortcut})',
  'ui.selection-toolbar.label': 'Selected text',
  'ui.selection-toolbar.add-note': 'Add note',
  'ui.selection-toolbar.private-note': 'Private note',
  'ui.selection-toolbar.copy': 'Copy',
  'ui.book-page.label': 'Book page',
  'ui.book-page.page': 'Page {folio}',
  'ui.page-spread.label': 'Page spread',
  'ui.diff-text.added': '[added:',
  'ui.diff-text.removed': '[removed:',
  'ui.diff-text.end': ']',
  'ui.diff-text.underlined': 'Underlined',
  'ui.diff-text.was-added': 'was added',
  'ui.diff-text.struck': 'struck through',
  'ui.diff-text.was-removed': 'was removed',
  'ui.field.required': 'Required',
  'ui.field.optional': 'Optional',
  'ui.password-field.show': 'Show',
  'ui.password-field.hide': 'Hide',
  'ui.password-field.show-password': 'Show password',
  'ui.password-field.hide-password': 'Hide password',
  'ui.password-field.strength': 'Strength:',
  'ui.password-field.too-short': 'Too short',
  'ui.password-field.weak': 'Weak',
  'ui.password-field.fair': 'Fair',
  'ui.password-field.good': 'Good',
  'ui.password-field.strong': 'Strong',
  'ui.search-field.placeholder': 'Search',
  'ui.search-field.label': 'Search',
  'ui.search-field.clear': 'Clear search',
  'ui.textarea.characters': 'characters',
  'ui.switch.on': 'On',
  'ui.switch.off': 'Off',
  'ui.code-input.recovery-key': 'Recovery key',
  'ui.code-input.group': 'Group {n} of {total}',
  'ui.file-drop.title': 'Drag a file here',
  'ui.file-drop.button': 'Choose file…',
  'ui.file-drop.drop': 'Drop to add',
} as const satisfies Catalog);

/** The English catalog's type: the keys and placeholders every catalog has. */
export type Messages = typeof EN;
